import {
    LogManager
} from './log-manager'
import {
    SharedFunctions
} from './shared-functions'
import {
    LogType,
    InsertType,
    InsertTypePosition,
    DebugURLParams,
    OverlayEventType,
    AnalyticsEventType,
    BidOriginSourceType,
    ComponentSourceType,
    ZoneModifications,
    PlayerMode,
    OsdBidOrderOption,
    PlayerEventLogFrequency,
    ZoneType,
    InsertOverrideType
} from '../../common/schemas/enums'
import {
    BidData,
    PrebidData,
    GAMData,
    TAMData,
    AnalyticsEventDispatcher
} from './analytics-event-dispatcher'
import {
    GroupConfig,
    PlayerConfig,
    Resource,
    InsertConfig,
    UserRegionInfo,
    OSDGroupConfig,
    UserDeviceInfo,
    AllowedInsert,
    ResourceBidParameters
} from '../../common/schemas/public'
import {
    setApiLm,
    fetchResourceData,
    initiatePageAnalysis,
    initiatePageLookup
} from '../services/api'
import {
    CustomTimeout
} from '../services/custom-timeout'
import {
    isNil
} from 'lodash-es'

export interface OSDProperties {
    cxoAdSlot: any
    cxoBidInfo: any
    cxAdUnits: {
        code: string
        mediaTypes: {
            banner: {
                sizes: number[][]
                pos?: number
            }
        }
        bids: any[]
        optiYield?: {
            adUnitPath: string
            divId: string
        }
    }[]
}

export interface OSDSupplyAccessConfig {
    osdRefresh?: Function,
    renderPrebidWin?: Function,
    availableAdSizes?: number[][],
    eventData?: {},
    slotRefreshParams: {
        gptAdUnitId?: string,
        bidderArray?: any[],
        gtagObject?: any,
        pbjsObject?: any,
        osdBidOrder?: any,
        allowGAMRefresh?: boolean
    }
    isPrebidAvailable?: boolean
}

export interface BannerParameters {
    insertType: InsertType
    bannerSize: number[]
    adxSize: number[]
}

export interface CurrentInsertMap {
    nativeOverlayCorner: InsertType
    nativeOverlayIcon: InsertType
    nativeOverlayFullwidth: InsertType
    nativeFlap: InsertType
    nativeWing: InsertType
    nativeSqueeze: InsertType
    nativeCustomSqueeze: InsertType
    nativeVideoSqueeze: InsertType
    displayOverlayCorner: InsertType
    displayOverlayFullwidth: InsertType
    displayFlap: InsertType
    displayWing: InsertType
    displaySqueeze: InsertType
    ctvOverlayCorner: InsertType
    ctvSqueeze: InsertType
}

export interface RegisteredWindowEvents {
    resize: EventListener,
    touchmove: EventListener,
    scroll: EventListener,
    message: EventListener,
    visibilitychange: EventListener
}

export interface WatchedElementProperties {
    width: number
    height: number
    top: number
    left: number
}

export interface CarouselData {
    active: boolean
    bidCount: number
    isClosed: boolean
    isCompleted: boolean
}

export interface AllowedBannerSizes {
    squeezeback: number[][]
    wing: number[][]
    wingTall: number[][]
    flap: number[][]
    overlayFullwidth: number[][]
    overlayCorner: number[][]
}

export interface CardinalDirections {
    top: string
    bottom: string
    left: string
    right: string
}

export interface DefaultInsertTypeMap {
    defaultType: InsertType
    mappedTypes: InsertType[]
}

export type CXInjectorWindowDebug = {
    [key in DebugURLParams]?: string
}

export enum CardinalDirectionsModes {
    AUTO = 'auto',
    ZERO_INSET = 'zero-inset',
    ADJUSTED_INSET = 'adjusted-inset'
}

export enum ManualBiddingEvents {
    INITIATE_BID = 'qx.initiate.bid'
}

export interface ElementDimensions {
    height: number
    width: number
    previousElementWidth: number
    previousWidth: number
    previousHeight: number
    x: number
    y: number
    top: number
    right: number
    bottom: number
    left: number
    transform: string[]
    winHeight: number
    winWidth: number
    scale: number
    elementScale: number
    isFloating: boolean
    isFullscreen: boolean
    insertPosition: string
    elementDimensionsStable: boolean
}

export interface OverlayUtilitiesInitializationValues {
    componentElement: HTMLElement,
    containerElement: HTMLElement,
    activeElement: () => HTMLElement,
    activeResourceData: () => Resource,
    bidLogicParams: () => BidLogicParameters,
    carouselResourceData: () => Resource,
    removeProhibitedInsertTypes: () => string[],
    playerMode: () => string,
    qElementId: () => string,
    cxoActive: () => boolean,
    destroyFunction: () => void,
    addManualBiddingEvents: () => void,
    setTimerBidding: (timerBidding: boolean) => void,
    setZIndexUpdate: (zIndex: string) => void
}

export interface BidLogicParameters {
    updateCXO: () => void,
    cancelRemakeADXInsertTimer: () => void,
    generateADXInsert: () => void,
    generateNativeInsert: () => void,
    generateOSDInsert: () => void,
    handleResourceResponse: (resourceData: any) => void,
    setNextAutomaticBidTime: () => void,
    isFullscreenVideoNode: boolean,
    isVideoOnPage: boolean,
    rebidBannerSizeAllowedDeviation: number,
    stowedBidResponse: any,
    preferredBannerSize: BannerParameters,
    cxWait: boolean,
    nobid: boolean,
    currentElementStatus: any,
    carouselResource: HTMLElement,
    videoElement: HTMLVideoElement,
    activeMediaId: string,
    activeLogicalVideoId: string,
    marketplaceId: string,
    storeId: string,
    bundleId: string
}

interface ComponentCustomTimers {
    osdRefreshRenderEndedTimer: CustomTimeout;
    clsTimer: CustomTimeout;
    manualBidRequestTimeout: CustomTimeout;
    framerateTimer: CustomTimeout;
    initiateBidRequestTimer: CustomTimeout;
    carouselBidTimer: CustomTimeout;
    automaticBidTimer: CustomTimeout;
    initiateManualBidRequestTimer: CustomTimeout;
    pageLookupTimeout: CustomTimeout;
}

const API_HOST = process.env.API_HOST

declare var owpbjs: any;
declare var IntentIqObject: any;

export class OverlayUtilities {
    private _logManager: LogManager = null;
    private _analyticsEventDispatcher = null;
    private _foundUID: string = null;
    private sharedFunctions: SharedFunctions;
    private osdProperties: OSDProperties = null;
    private groupId: string = null;
    public debugLog: Function;
    private _currentInserts: CurrentInsertMap = {
        nativeOverlayCorner: InsertType.OVERLAY_HORIZONTAL_SLIDE_COLOR_DETECT,
        nativeOverlayIcon: InsertType.OVERLAY_SLIDE_ICON_COLOR_DETECT,
        nativeOverlayFullwidth: InsertType.OVERLAY_FULLWIDTH_ICON_COLOR_DETECT,
        nativeFlap: InsertType.BLEEDOVER_FLAP_REVOLVING_HEADLINE,
        nativeWing: InsertType.BLEEDOVER_WING_PRECISION_SIZE,
        nativeSqueeze: InsertType.SQUEEZE_BACK_DYNAMIC_FULLWIDTH,
        nativeCustomSqueeze: InsertType.SQUEEZE_BACK_CUSTOM_FULL_BACKGROUND,
        nativeVideoSqueeze: InsertType.SQUEEZE_BACK_CUSTOM_VIDEO_BACKGROUND,
        displayOverlayCorner: InsertType.DISPLAY_OVERLAY_CORNER_SLIDE,
        displayOverlayFullwidth: InsertType.DISPLAY_FULLWIDTH_HORIZONTAL_OVERLAY,
        displayFlap: InsertType.DISPLAY_BLEEDOVER_FLAP_FLOATING,
        displayWing: InsertType.DISPLAY_BLEEDOVER_WING_MINI,
        displaySqueeze: InsertType.DISPLAY_SQUEEZE_BACK_BANNER,
        ctvOverlayCorner: InsertType.CTV_OVERLAY_CORNER_CLEAN_QR,
        ctvSqueeze: InsertType.CTV_SQUEEZE_BACK_CUSTOM_QR,
      }
    private componentName: ComponentSourceType;
    private componentStartTime: number = (new Date()).getTime();
    private _activeOverlayRuntime: number = 0;
    private _activeOverlayDate: number = this.componentStartTime;
    public activeCarouselData: CarouselData = {
        active: false,
        bidCount: 0,
        isClosed: false,
        isCompleted: false
    }
    public containerViewabilityValue: number = 0;
    public osdConfig: OSDSupplyAccessConfig = null;
    private indexingInitiated: boolean = false;
    private retrievedPageContext: {[key: string]: any};
    private _groupConfig: GroupConfig;
    private _groupConfigsProcessed: boolean = false;
    public _playerConfig: PlayerConfig = null;
    private componentElement: HTMLElement;
    private getCarouselResourceData: () => Resource;
    private getActiveResourceData: () => Resource;
    private getBidLogicParams: () => BidLogicParameters;
    private getActiveElement: () => HTMLElement;
    private getPlayerMode: () => string;
    private getQElementId: () => string;
    private getCXOActive: () => boolean;
    private timerBidding: (timerBidding: boolean) => void;
    private zIndexUpdate: (zIndex: string) => void;
    private addManualBiddingEvents: () => void;
    public forceNextInsert: boolean;
    public forceNextManualBid: boolean = false;
    public forceNextBid: boolean;
    public forceNextRebid: boolean = false;
    private requestedBannerSizes: number[][] = [];
    public cxWaitInitiateBid: boolean = false;
    public isOnStream:boolean = true;
    private containerElement: HTMLElement;
    private timers: ComponentCustomTimers = {
        osdRefreshRenderEndedTimer: new CustomTimeout(() => { this.renderEndedTimeout() }, 0, 'CXO_OSD_REFRESH OSD Refresh renderEnded Timer', this.logManager, LogType.QA),
        clsTimer: new CustomTimeout(() => { this.checkCLSValue() }, 0, 'Check CLS Value Timer'),
        manualBidRequestTimeout: new CustomTimeout(() => {}, 0, 'Manual Bid Timeout'),
        framerateTimer: new CustomTimeout(() => {}, 0, "Framerate Watch Timer"),
        carouselBidTimer: new CustomTimeout(() => { this.initiateCarouselBid() }, 0, 'Carousel Bid Timer', this.logManager, LogType.CXO),
        initiateBidRequestTimer: new CustomTimeout(() => { this.initiateBidRequest() }, 0, 'Initiate Bid Request Timer', this.logManager, LogType.CXO),
        automaticBidTimer: new CustomTimeout(() => {
            this.forceNextInsert = true;
            this.forceNextBid = true;
            this.rebidCount = 0;
            this.resetCarouselData();
            this.timers.initiateBidRequestTimer.execute();
          }, 0, 'Automatic Bid Timer', this.logManager, LogType.QA),
        initiateManualBidRequestTimer: new CustomTimeout(() => this.initiateManualBidRequest(), 0, 'Initiate Manual Bid Request Timer', this.logManager, LogType.CXO),
        pageLookupTimeout: new CustomTimeout(() => { this.requestPageLookup() }, 0, 'Request Page Lookup Timeout', this.logManager, LogType.CXO)
    }
    private previousClsValue: number = 0;
    private clsValue: number = 0;
    private _eidsIdentityHub: any;
    private _eidsintentIQ: any;
    private _tcString: string;
    private _uspString: string;
    private _gdprApplies: number = 0;
    private _gppString: string;
    private _gppSids: number[];
    private adOpportunityIdentifier: string;
    private bidRequestIdentifier: string;
    private analyticsSessionIdentifier: string;
    private destroyFunction: Function = ()=>{};
    private removeProhibitedInsertTypes: Function = () => {};
    private frameArray: number[] = [];
    
    public isManualBidding: boolean = false;
    public isCTV: boolean = false;
    private outbrainBidCount: number = 0;
    public outbrainToken: string = null;
    public bidCount: number = 0;
    public rebidCount: number = 0;
    public impressionCount: number = 0;
    public allowedUnsuccessfulBids: number = 1000;
    public currentDimensions: ElementDimensions = {
        height: 0,
        width: 0,
        previousElementWidth: 0,
        previousHeight: 0,
        previousWidth: 0,
        x: 0,
        y: 0,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        transform: ['100', '100'],
        winHeight: 0,
        winWidth: 0,
        scale: 1,
        elementScale: 1,
        isFloating: false,
        isFullscreen: false,
        insertPosition: '',
        elementDimensionsStable: true
    }
    public bidderOrder: BidOriginSourceType[] = [
        BidOriginSourceType.ADK,
        BidOriginSourceType.ADK,
        BidOriginSourceType.ADK,
        BidOriginSourceType.ADX,
        BidOriginSourceType.ADK,
        BidOriginSourceType.ADK,
        BidOriginSourceType.ADK,
        BidOriginSourceType.ADK,
        BidOriginSourceType.ADK,
        BidOriginSourceType.ADK,
        BidOriginSourceType.OUTBRAIN,
        BidOriginSourceType.REV_CONTENT
    ]
    public osdBidOrder: any = null;
    private defaultInsertTypeMapping: DefaultInsertTypeMap[] = [
        {defaultType:InsertType.DEFAULT_FLAP, mappedTypes:[this.currentInserts.nativeFlap]},
        {defaultType:InsertType.DEFAULT_WING, mappedTypes:[this.currentInserts.nativeWing]},
        //{defaultType:InsertType.DEFAULT_SQUEEZEBACK, mappedTypes:[this.currentInserts.nativeSqueeze]},
        {defaultType:InsertType.DEFAULT_SQUEEZEBACK, mappedTypes:[this.currentInserts.nativeSqueeze, this.currentInserts.nativeCustomSqueeze, this.currentInserts.displaySqueeze, this.currentInserts.ctvSqueeze]},
        {defaultType:InsertType.DEFAULT_SQUEEZEBACK_CUSTOM, mappedTypes:[this.currentInserts.nativeCustomSqueeze, this.currentInserts.ctvSqueeze]},
        {defaultType:InsertType.DEFAULT_OVERLAY_FULLWIDTH, mappedTypes:[this.currentInserts.nativeOverlayFullwidth]},
        {defaultType:InsertType.DEFAULT_OVERLAY_CORNER_ICON, mappedTypes:[this.currentInserts.nativeOverlayIcon]},
        {defaultType:InsertType.DEFAULT_OVERLAY_CORNER_NO_ICON, mappedTypes:[this.currentInserts.nativeOverlayCorner]},
        {defaultType:InsertType.DEFAULT_OVERLAY_CORNER, mappedTypes:[this.currentInserts.nativeOverlayCorner, this.currentInserts.nativeOverlayIcon, this.currentInserts.ctvOverlayCorner]}
      ]
    public allowedBannerSizes: AllowedBannerSizes = {
      squeezeback: [
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ],
      wing: [
        [300, 250],
        [336, 280],
        [300, 600],
        [300, 1080]
      ],
      wingTall: [
        [300, 250],
        [300, 600],
        [160, 600]
      ],
      flap: [
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ],
      overlayFullwidth: [
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ],
      overlayCorner: [
        [234, 60],
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ]
    }
    public allowedOnstreamDirectDesktopSizes: AllowedBannerSizes = {
      squeezeback: [
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ],
      wing: [
        [300, 250],
        [336, 280],
        [300, 600],
        [300, 1080]
      ],
      wingTall: [
        [300, 250],
        [300, 600],
        [160, 600]
      ],
      flap: [
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ],
      overlayFullwidth: [
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ],
      overlayCorner: [
        [234, 60],
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ]
    }
    public allowedOnstreamDirectMobileSizes: AllowedBannerSizes = {
      squeezeback: [
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ],
      wing: [
        [300, 250],
        [336, 280],
        [300, 600],
        [300, 1080]
      ],
      wingTall: [
        [300, 250],
        [300, 600],
        [160, 600]
      ],
      flap: [
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ],
      overlayFullwidth: [
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ],
      overlayCorner: [
        [234, 60],
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90]
      ]
    }
    public allowedADXBannerSizes: AllowedBannerSizes = {
      squeezeback: [
        [88, 31],
        [292, 30],
        [300, 50],
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90],
        [1024, 90]
      ],
      wing: [
        [120, 60],
        [200, 200],
        [300, 250],
        [336, 280],
        [480, 320],
        [580, 400],
        [768, 1024]
      ],
      wingTall: [
        [200, 200],
        [300, 250],
        [250, 250],
        [320, 400],
        [240, 400],
        [320, 480],
        [200, 446],
        [300, 600],
        [160, 600],
        [120, 600],
        [768, 1024]
      ],
      flap: [
        [88, 31],
        [292, 30],
        [300, 50],
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90],
        [1024, 90]
      ],
      overlayFullwidth: [
        [88, 31],
        [292, 30],
        [300, 50],
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90],
        [1024, 90]
      ],
      overlayCorner: [
        [88, 31],
        [292, 30],
        [300, 50],
        [320, 50],
        [468, 60],
        [728, 90],
        [970, 90],
        [1024, 90]
      ]
    }

    constructor(gid: string, componentName: ComponentSourceType) {
        this.groupId = gid;
        this.logManager = new LogManager();
        this.logManager.componentSource = this.componentName = componentName;
        this.logManager.debugLogGroupId = this.groupId;
        this.sharedFunctions = new SharedFunctions(this.logManager);
        this.debugLog = this.sharedFunctions.debugLog;
        const allowedEventsPerMinute: number = (parseInt(this.logManager.getDebugParamValue(DebugURLParams.ALLOWED_EVENTS_PER_MINUTE)) > 0)?parseInt(this.logManager.getDebugParamValue(DebugURLParams.ALLOWED_EVENTS_PER_MINUTE)):150;
        this.analyticsEventDispatcher = new AnalyticsEventDispatcher(this.logManager, this.shutdownComponent.bind(this, 'Event frequency too high.'), allowedEventsPerMinute);

        if(parseInt(this.logManager.getDebugParamValue(DebugURLParams.ALLOWED_FAILED_BIDS)) > 0){
            const allowedFailedBids: number = parseInt(this.logManager.getDebugParamValue(DebugURLParams.ALLOWED_FAILED_BIDS));
            this.debugLog(`Setting OSD failed bid limit to ${allowedFailedBids} using URL parameter ${DebugURLParams.ALLOWED_FAILED_BIDS}`, [LogType.QA, LogType.CXO]);
            this.allowedUnsuccessfulBids = allowedFailedBids;
        }
        const logModeParam = this.logManager.getDebugParamValue(DebugURLParams.CX_LOG_MODE);
        if (logModeParam !== null) {
            this.logManager.logMode = logModeParam;
            if (this.logManager.logMode.includes('most')) {
                this.logManager.logMode += 'qa-info-boot-insert-error-boot-important';
            }
        }
        if (this.logManager.logMode === LogType.ALL) {
            this.debugLog('Logging type ALL - Verbose logging mode ON', LogType.QA);
        }
        const vals = Object.values(LogType);
        for (let t in vals) {
            if (this.logManager.logMode.includes(LogType.QA) || this.logManager.logMode.includes(LogType.CXO)) {
                this.debugLog(`CX ${vals[t]} logs on`, vals[t]);
            }
        }

        setApiLm(this.logManager);
        this.timers.framerateTimer.reset(1000, ()=>{this.debugLog(`${this.frameRate.toFixed(3)} fps`, LogType.CXO)});
        this.timers.framerateTimer.repeatCount = 60*60*24;
        this.captureAnimationFrame();
    }

    initializeRequiredData(initializeData: OverlayUtilitiesInitializationValues) {
        this.componentElement = initializeData.componentElement;
        this.getActiveElement = initializeData.activeElement;
        this.containerElement = initializeData.containerElement;
        this.getActiveResourceData = initializeData.activeResourceData;
        this.getCarouselResourceData = initializeData.carouselResourceData;
        this.getBidLogicParams = initializeData.bidLogicParams;
        this.getPlayerMode = initializeData.playerMode;
        this.getQElementId = initializeData.qElementId;
        this.timerBidding = initializeData.setTimerBidding;
        this.zIndexUpdate = initializeData.setZIndexUpdate;
        this.destroyFunction = initializeData.destroyFunction;
        this.addManualBiddingEvents = initializeData.addManualBiddingEvents;
        this.removeProhibitedInsertTypes = initializeData.removeProhibitedInsertTypes;
        this.getCXOActive = initializeData.cxoActive;

        this.analyticsSessionIdentifier = this.generateUniqueId;
        this.logManager.analyticsSessionId = this.analyticsSessionIdentifier;
        this.analyticsEventDispatcher.assignEventsSessionId(this.analyticsSessionId);
        this.analyticsEventDispatcher.assignEventsGroupId(this.groupId);

        this.rebidCount = 0;
        try{
            this.createPerformanceObserver();
        }catch(err){
            this.debugLog("Error creating PerformanceObserver", LogType.IMPORTANT);
        }
        
        this.captureAdConcentration();
    }

    requestPageLookup(){
        this.debugLog('Requesting page lookup', LogType.QA);
        if(!Boolean(this.retrievedPageContext)){
          initiatePageLookup(this.groupId, this.sharedFunctions.getPageLookupData()).then((res) =>{
            if(res === null || typeof res === 'undefined'){
              this.debugLog([`Page Lookup Results (null or undefined): `, res], LogType.QA);
              this.requestPageAnalysis();
            }else{
              this.debugLog([`Page Lookup Results: `, res], LogType.QA);
              this.retrievedPageContext = res || null;
              if(Boolean(this.retrievedPageContext)){
                this.timers.pageLookupTimeout.cancel();
              }
            }
          });
        }else{
          this.timers.pageLookupTimeout.cancel();
          this.debugLog([`Page Lookup results already received: `, this.retrievedPageContext], LogType.QA);
        }
      }
    
    requestPageAnalysis(){
        this.debugLog('Requesting page analysis', LogType.QA);
        if(!this.indexingInitiated){
            this.indexingInitiated = true;
            this.analyticsEventDispatcher.sendOverlayEvent(AnalyticsEventType.OVERLAY_PAGE_INDEX, BidOriginSourceType.PLATFORM, document.location.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), this.elapsedOverlayTime, this.activeOverlayTime, this.containerViewabilityValue);
            initiatePageAnalysis(this.groupId, this.sharedFunctions.getPageContextData()).then(res =>{
            this.debugLog(`Page Analysis Results: ${res}`, LogType.QA);
            if(res !== 201 && res !== 200){
                this.indexingInitiated = false;
            }
            });
        }else{
            this.debugLog([`Page contextual analysis already initiated for current page.`], LogType.QA);
        }
    }

    attemptPageAnalysis(){
        if(this.sharedFunctions.checkPercentageOutcome(this.groupConfig.platformPageIndexingPercentage)){
            this.debugLog(`Attempting page analysis. Indexing percentage: ${this.groupConfig.platformPageIndexingPercentage}%`, LogType.QA);
            this.timers.pageLookupTimeout.execute();
            this.timers.pageLookupTimeout.reset(5000);
            this.timers.pageLookupTimeout.repeatCount = 1000;
        }else{
            this.debugLog('Will not attempt page analysis due to failed percentage check', LogType.QA);
        }
    }
    
    captureAdConcentration(){
        let adNodes:NodeListOf<HTMLElement> = document.querySelectorAll('iframe[id*=google_ads_iframe],a[href*=doubleclick]');
        let adList:string[] = [];
        const adDebugObject:{[key:string]:any} = {
            "adIdList": adList,
            "viewportWidth": window.innerWidth,
            "viewportHeight": window.innerHeight,
            "pageWidth": document.documentElement.scrollWidth,
            "pageHeight": document.documentElement.scrollHeight
        };
        adNodes.forEach((ad:HTMLElement) => {
            let adRect:DOMRect = ad.getBoundingClientRect();
            let adName:string = ad.id || "dc-y"+ad.getBoundingClientRect().y+"-a[href*="+(ad as HTMLAnchorElement).href.substring((ad as HTMLAnchorElement).href.length-100)+"]";
            if(adRect.width > 0 && adRect.height > 0){
                adList.push(adName);
                adDebugObject[adName] = {
                    "x": adRect.x + window.scrollX,
                    "y": adRect.y + window.scrollY,
                    "width": adRect.width,
                    "height": adRect.height,
                    "title": ad.title
                }
            }
        })
        this.analyticsEventDispatcher.sendOverlayEvent(AnalyticsEventType.OVERLAY_CAPTURE_AD_CONCENTRATION, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), this.elapsedOverlayTime, this.activeOverlayTime, this.containerViewabilityValue, {adList: adDebugObject});
    }

    configureManualBidding(){
        this.isManualBidding = true;
        this.manualBidRequestTimeout = new CustomTimeout(() => {}, 0, 'Manual bid request timeout')
        this.groupConfig.persistentInserts = false;
        this.groupConfig.carouselEnabled = false;
        this.groupConfig.insertFrequency = 0;
    }

    shutdownComponent(reason:string){
        this.destroyFunction();
        this.analyticsEventDispatcher.sendOverlayEvent(AnalyticsEventType.OVERLAY_FORCED_SHUTDOWN, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), this.elapsedOverlayTime, this.activeOverlayTime, this.containerViewabilityValue, { "reason": reason});
    }

    addSync(zoneId: string) {
        const syncVarName: string = `cxsyncadded-${zoneId}`;
        if (typeof window[`${syncVarName}`] === 'undefined' || window[`${syncVarName}`] === null || !window[`${syncVarName}`]) {
            window[`${syncVarName}`] = true;

            let removeSyncFrameTimeout: ReturnType<typeof setTimeout>

            const removeSync = () => {
                if (!isNil(cxusersync.parentElement)) {
                    cxusersync.parentElement.removeChild(cxusersync);
                    this.debugLog(`addSync: removing sync '${zoneId}' iframe from page.`, [LogType.QA, LogType.CXO]);
                }
            }

            window.addEventListener("message", (event) => {
                if (
                    !isNil(event) &&
                    !isNil(event.data) &&
                    typeof event.data === 'string' &&
                    event.data.indexOf("akuid:") > -1
                ) {
                    const akuid = event.data.split(":")[1];
                    this._foundUID = akuid;
                    document.cookie = "akuid=" + akuid + "; SameSite=None; Secure";
                    this.debugLog("utils addSync: akuid received -- set akuid = " + akuid, [LogType.CXO, LogType.QA]);
                    clearTimeout(removeSyncFrameTimeout);
                    removeSync();
                }
            })

            const cxusersync = document.createElement('iframe')
            const redirect = encodeURIComponent(`${API_HOST}/sync?akuid=${window.location.origin}|{UID}`)
            const adksyncUrl: string = (window.location?.protocol?.indexOf('file')===0)?`https://sync.adkernel.com/user-sync?zone=${zoneId}&r=${redirect}`:`//sync.adkernel.com/user-sync?zone=${zoneId}&r=${redirect}`
            cxusersync.setAttribute('src', adksyncUrl)
            cxusersync.setAttribute('style', 'display:none')
            cxusersync.setAttribute('width', '0')
            cxusersync.setAttribute('height', '0')
            cxusersync.setAttribute('frameborder', '0')
            this.containerElement.appendChild(cxusersync)

            removeSyncFrameTimeout = setTimeout(removeSync, 10000);

            this.debugLog(`addSync: cx-sync added to container. window['${syncVarName}']=true`, [LogType.QA, LogType.CXO]);
        } else {
            this.debugLog(`addSync: cx-sync '${syncVarName}' already attached to page. Will not re-initialize.`, [LogType.QA, LogType.CXO]);
        }
    }

    getGPPData(bidding: boolean = false) {
        let topmostWindow: Window = this.availableTopWindow;
        if (topmostWindow.hasOwnProperty('__gpp')) {
            const __gpp: any = topmostWindow['__gpp'];
            try {
                const { applicableSections, gppString } = __gpp('getGPPData')
                this._gppString = gppString;
                this._gppSids = applicableSections
                this.debugLog(`Collected GPPData: gppString=${this._gppString}, gppSids=${this._gppSids}`, [LogType.QA]);
            } catch {
                this.debugLog([`__gpp is not properly configured`], [LogType.QA]);
            }
        } else {
            if (!bidding) {
                this.debugLog([`__gpp is not found`], [LogType.QA]);
            } else {
                this.debugLog([`__gpp is not found during bid request`], [LogType.CXO]);
            }
        }
    }

    getTCData(bidding: boolean = false) {
        let topmostWindow: Window = this.availableTopWindow;
        if (topmostWindow.hasOwnProperty('__tcfapi')) {
            const __tcfapi: any = topmostWindow['__tcfapi'] ?? null;
            if (__tcfapi !== null) {
                __tcfapi('ping', 2, (pingData) => {
                    this.debugLog([`pingData:`, pingData], [LogType.QA]);
                    if (pingData.gdprApplies) {
                        this._gdprApplies = 1;
                        __tcfapi('getTCData', 2, (tcdata) => { this._tcString = tcdata.tcString });
                        this.debugLog(`Collected TCString: ${this._tcString}`, [LogType.QA]);
                    } else {
                        this.debugLog([`GDPR does not apply`], [LogType.QA]);
                    }
                })
            }
        } else {
            if (!bidding) {
                this.debugLog([`__tcfapi is not found`], [LogType.QA]);
            } else {
                this.debugLog([`__tcfapi is not found during bid request`], [LogType.CXO]);
            }
        }
    }

    getUSPData(bidding: boolean = false) {
        let topmostWindow: Window = this.availableTopWindow;
        if (topmostWindow.hasOwnProperty('__uspapi')) {
            const __uspapi: any = topmostWindow['__uspapi'] ?? null;
            if (__uspapi !== null) {
                __uspapi('getUSPData', 1, (uspData) => { this._uspString = uspData?.uspString });
                this.debugLog(`Collected uspString: ${this._uspString}`, [LogType.QA]);
                this._uspString ??= null;
            }
        } else {
            if (!bidding) {
                this.debugLog([`__uspapi is not found`], [LogType.QA]);
            } else {
                this.debugLog([`__uspapi is not found during bid request`], [LogType.CXO]);
            }
        }
    }

    registerIdentityHub() {
        const purl = window.location.href;
        const url = '//ads.pubmatic.com/AdServer/js/pwt/161089/5500';
        let profileVersionId = '';
        if (purl.indexOf('pwtv=') > 0) {
            const regexp = /pwtv=(.*?)(&|$)/g;
            const matches = regexp.exec(purl);
            if (matches.length >= 2 && matches[1].length > 0) {
                profileVersionId = '/' + matches[1];
            }
        }
        const wtads = document.createElement('script');
        wtads.async = true;
        wtads.type = 'text/javascript';
        wtads.src = url + profileVersionId + '/pwt.js';
        const node = document.getElementsByTagName('script')[0];
        node.parentNode.insertBefore(wtads, node);

        const initializeIdentityHub = () => {
            if (typeof owpbjs !== 'undefined' && owpbjs !== null) {
                this._eidsIdentityHub = owpbjs?.getUserIdsAsEids?.() ?? null;
                this.debugLog([`Identity Hub UIDs received:`, this._eidsIdentityHub], [LogType.QA, LogType.CXO]);
            } else {
                this.debugLog(`Cannot find Identity Hub Object 'owpbjs'. Will try again.`, [LogType.QA, LogType.CXO]);
                if (this.getCXOActive()) {
                    setTimeout(initializeIdentityHub, 1000);
                }
            }
        }
        initializeIdentityHub();
    }

    registerIntentIQ() {
        const storedIntentIQInfo: string = localStorage.getItem('cxIntentIQ_1401077057');

        const retrievalTime: number = (!isNil(storedIntentIQInfo)) ? JSON.parse(storedIntentIQInfo).retrievalTime ?? 0 : 0;
        const elapsedTimeFromRetrieval: number = (new Date()).getTime() - retrievalTime;
        let seconds: number = Math.floor(elapsedTimeFromRetrieval / 1000);
        let minutes: number = Math.floor(seconds / 60);
        let hours: number = Math.floor(minutes / 60);
        seconds = seconds % 60;
        minutes = minutes % 60;

        //Check if Intent IQ information was retrieved over 8 hours ago.
        if (elapsedTimeFromRetrieval > 8 * 60 * 60 * 1000) {
            const timeElapsedDebug: string = (hours > 1000) ? 'Intent IQ information has not been stored locally on this machine.' : `Intent IQ information retrieved ${hours} hours, ${minutes} minutes, and ${seconds} seconds ago. Data is expired.`;
            this.debugLog(`${timeElapsedDebug} Intent IQ information will be retrieved.`, [LogType.QA, LogType.CXO]);
            if (document.querySelector("script[src*='IIQUniversalID.js']") === null) {
                this.debugLog('Intent IQ script not found. Adding Intent IQ script to page.', [LogType.CXO]);
                const urlIntentIQ = process.env.SERVER_ROOT + '/assets/global-js/IIQUniversalID.js';
                const scriptIntentIQ = document.createElement('script');
                scriptIntentIQ.async = true;
                scriptIntentIQ.type = 'text/javascript';
                scriptIntentIQ.src = urlIntentIQ;
                document.head.appendChild(scriptIntentIQ);
            }

            const initializeIntentIQ = () => {
                if (typeof IntentIqObject !== 'undefined' && IntentIqObject !== null) {
                    // A unique IntentIQ id object name (i.e use your partner name / id)
                    const intentIq_1401077057 = new IntentIqObject({
                        // Please contact our team to get you partner id
                        partner: 1401077057,
                        // Default 12 hour
                        refreshInMillis: 43200000,
                        // Default 3 seconds
                        timeoutInMillis: 5000,
                        // Determine if the http request should be async to avoid blocking page loading
                        isAsyncServerRequest: true,
                        // Callback function that will get the id data
                        callback: (data) => {
                            this._eidsintentIQ = data?.eids;
                            this.debugLog(["Intent IQ callback method received. Will put in local storage: ", data], [LogType.QA, LogType.CXO]);
                            this.storeIntentIQData(data);
                        },
                        //enable enriching all outgoing POST bid requests
                        enhanceRequests: true,
                        //additional metedata
                        sourceMetaData: 'my_meta_data',
                        //operational mode: BidEnhancement, Sync , Both
                        mode: 'Both',
                        //partner's first party client id (value) 
                        partnerClientId: this.groupId,
                        //partner's first party client id type: 3rd party cookie = 0, IDFV = 1, first party id = 3, MAID/AAID = 4
                        partnerClientIdType: 3,
                        //Setting group percentage distribution for AB testing
                        //abPercentage: 90,
                        //manually configure AB testing by defining weather this is a test group
                        //isInTestGroup: true,
                    });
                    intentIq_1401077057?.getIntentIqData();
                } else {
                    this.debugLog('Cannot find IntentIqObject. Will try again.', [LogType.QA, LogType.CXO]);
                    setTimeout(initializeIntentIQ, 1000);
                }
            }
            initializeIntentIQ();
        } else {
            this._eidsintentIQ = JSON.parse(storedIntentIQInfo)?.eids;
            this.debugLog([`Intent IQ information found in local storage with an age of ${hours} hours, ${minutes} minutes, and ${seconds} seconds.`, JSON.parse(storedIntentIQInfo)], [LogType.CXO, LogType.QA]);
        }
    }

    storeIntentIQData(data) {
        data.retrievalTime = (new Date()).getTime();
        localStorage.setItem('cxIntentIQ_1401077057', JSON.stringify(data));
    }

    get foundUID(): string {
        this._foundUID ??= "NOT_FOUND";
        if (this._foundUID === "NOT_FOUND") {
            let name = "akuid=";
            let decodedCookie = decodeURIComponent(document.cookie);
            let ca = decodedCookie.split(';');
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) == ' ') {
                    c = c.substring(1);
                }
                if (c.indexOf(name) == 0) {
                    this._foundUID = c.substring(name.length, c.length);
                }
            }
            this.debugLog(`Attempting to find UID. Current identified UID: ${this._foundUID}.`, [LogType.CXO]);
        }
        return this._foundUID;
    }

    createPerformanceObserver() {
        let clsInitiated: boolean = false;
        let sessionEntries: any[] = [];
        let sessionValue: number = 0;

        new PerformanceObserver((entryList) => {
            for (const entry of entryList.getEntries()) {
                const clsEntry = (entry as any);
                // Only count layout shifts without recent user input.
                if (!clsEntry.hadRecentInput) {
                    const firstSessionEntry = sessionEntries[0];
                    const lastSessionEntry = sessionEntries[sessionEntries.length - 1];

                    // If the entry occurred less than 1 second after the previous entry and
                    // less than 5 seconds after the first entry in the session, include the
                    // entry in the current session. Otherwise, start a new session.
                    if (sessionValue &&
                        clsEntry.startTime - lastSessionEntry.startTime < 1000 &&
                        clsEntry.startTime - firstSessionEntry.startTime < 5000) {
                        sessionValue += clsEntry.value;
                        sessionEntries.push(clsEntry);
                    } else {
                        sessionValue = clsEntry.value;
                        sessionEntries = [clsEntry];
                    }

                    // If the current session value is larger than the current CLS value,
                    // update this.clsValue
                    if (sessionValue > this.clsValue) {
                        this.clsValue = sessionValue;
                        if (clsInitiated) {
                            this.checkCLSValue();
                        }
                    }
                }
            }
            // Send the CLS INIT event if this is the first major CLS event to occur
            if (!clsInitiated) {
                const roundedInitialCLS = +this.clsValue.toFixed(3);
                if (roundedInitialCLS > 0) {
                    this.analyticsEventDispatcher.sendOverlayEvent(AnalyticsEventType.CLS_INIT, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), this.elapsedOverlayTime, this.activeOverlayTime, this.containerViewabilityValue, { "clsValue": roundedInitialCLS });
                    this.previousClsValue = this.clsValue;

                    clsInitiated = true;
                    this.timers.clsTimer.reset(1000);
                }
            }
        }).observe({ type: 'layout-shift', buffered: true });
    }

    cancelAllTimers() {
        for (var t in this.timers) {
            this.debugLog(`Canceling timer '${this.timers[t].name}'`, [LogType.QA]);
            this.timers[t].cancel();
        }
    }

    checkCLSValue() {
        let clsChange = +(this.clsValue - this.previousClsValue).toFixed(2);
        const roundedCLSValue = +this.clsValue.toFixed(3);

        //Only generate a player-event if the CLS has >= 0.01 change
        if (this.timers.clsTimer.timeRemainingInSeconds == 0 && clsChange > 0) {
            this.debugLog("CLS value has been increased by: " + clsChange + "\nTotal CLS after increase: " + roundedCLSValue, [LogType.QA, LogType.CXO]);
            this.analyticsEventDispatcher.sendOverlayEvent(AnalyticsEventType.CLS_SHIFT_OBSERVED, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), this.elapsedOverlayTime, this.activeOverlayTime, this.containerViewabilityValue, { "clsValue": roundedCLSValue });
            this.previousClsValue = this.clsValue;
            this.timers.clsTimer.reset(1000);
        }
    }

    initializeIntersectionObserver() {
        try{
            let threshholdArray = [];
            for (var i = 0; i <= 100; i++) {
                threshholdArray.push(i / 100);
            }
            let observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    this.containerViewabilityValue = entry.intersectionRatio;
                });
            }, { threshold: threshholdArray });
            observer.observe(this.containerElement);
        }catch(err){
            this.debugLog('Unable to initialize IntersectionObserver for overlay. IntersectionObserver is not supported on this browser.', [LogType.CXO, LogType.QA, LogType.ERROR]);
        }
    }

    set logManager(logger: LogManager) {
        this._logManager = logger;
    }

    get logManager(): LogManager {
        return this._logManager;
    }

    set analyticsEventDispatcher(ald: AnalyticsEventDispatcher) {
        this._analyticsEventDispatcher = ald;
    }

    get analyticsEventDispatcher(): AnalyticsEventDispatcher {
        return this._analyticsEventDispatcher;
    }

    findOnpagePBJS(): Object {
        let pbjsName: string = (!!this.groupConfig?.prebidObjectIdentifier) ? this.groupConfig.prebidObjectIdentifier : null;
        let pbjsReturnWindowObject: any = null;
        if(pbjsName !== null){
            let pbjsWindowObject: any = window;
            let pbjsSplitIdentifier: string[] = pbjsName.split('.');
            for(let str in pbjsSplitIdentifier){
                pbjsWindowObject = pbjsWindowObject?.[pbjsSplitIdentifier[str]];
            }
            if(Boolean(pbjsWindowObject)){
                this.debugLog(`findOnpagePBJS Prebid object name ${pbjsName} defined in zone config and found on page.`, [LogType.CXO, LogType.QA]);
                pbjsReturnWindowObject = pbjsWindowObject;
            }else{
                this.debugLog(`findOnpagePBJS Prebid object name ${pbjsName} defined in zone config but is not found on page. Cannot use prebid object ${pbjsName}`, [LogType.CXO, LogType.QA]);
                pbjsName = null;
            }
        }
        pbjsName ??= (window['pbjs']?.adUnits && window['_pbjsGlobals']?.includes('pbjs')) ? 'pbjs' : window['_pbjsGlobals']?.find(name => name !== 'owpbjs' && name !== 'pbjs');
        pbjsName ??= (typeof window['owpbjs']?.adServers !== 'undefined') ? 'owpbjs' : null;
        if (window[pbjsName] != null) {
            this.debugLog(`findOnpagePBJS Compatible prebid object found on page with identifier '${pbjsName}'`, [LogType.CXO, LogType.QA, LogType.TAM]);
            pbjsReturnWindowObject = window[pbjsName];
        }
        return pbjsReturnWindowObject;
    }

    processGroupConfig(){
        const logConfig = JSON.stringify(this.groupConfig);
        let configsGroup: string = this.groupId;
        const overrideConfigsGroupIdParam: string = this.logManager.getDebugParamValue(DebugURLParams.OVERRIDE_CONFIGS_GROUP_ID);
        if(overrideConfigsGroupIdParam !== null){
          configsGroup = (overrideConfigsGroupIdParam == null || overrideConfigsGroupIdParam === '' || overrideConfigsGroupIdParam === 'true')?'internal':overrideConfigsGroupIdParam;
        }
        this.debugLog([`Group configs for group '${configsGroup}' received from bootstrapper: `, JSON.parse(logConfig)], [LogType.QA, LogType.CXO, LogType.TAM]);
    
        if(!this.getCXOActive()){
          this.debugLog([`CXO has been destroyed after group configs pull. Will not continue group config processing.`], [LogType.CXO, LogType.QA, LogType.IMPORTANT, LogType.TAM]);
        }else if(isNil(this.groupConfig?.insertConfig)){
          this.debugLog([`Group configs for '${configsGroup}' received incomplete configurations from environment ${API_HOST}`], [LogType.CXO, LogType.QA, LogType.IMPORTANT, LogType.TAM]);
          this.destroyFunction();
          return;
        }else if (this.isBlockedRegion()) {
          this.debugLog([`Region is blocked due to geo blocking policy for group ${configsGroup}:`, JSON.parse(logConfig)], [LogType.CXO, LogType.QA]);
          this.destroyFunction();
          this.analyticsEventDispatcher.sendOverlayEvent(AnalyticsEventType.OVERLAY_GEO_BLOCKED_REGION, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), this.elapsedOverlayTime, this.activeOverlayTime, this.containerViewabilityValue);
          return;
        }else{
          this.groupConfig.zoneModifications ??= [];
          if(!isNil(this.logManager.getDebugParamValue(DebugURLParams.ZONE_MODIFICATIONS_SET))){
            const zoneMods: string = this.logManager.getDebugParamValue(DebugURLParams.ZONE_MODIFICATIONS_SET);
            this.debugLog(`Setting zone modifications through debug parameter to: ${zoneMods}`, [LogType.CXO, LogType.QA]);
            this.groupConfig.zoneModifications = [];
            zoneMods.split('|').forEach(zm => this.groupConfig.zoneModifications.push(zm));
          }else if(!isNil(this.logManager.getDebugParamValue(DebugURLParams.ZONE_MODIFICATIONS_ADD))){
            const zoneMods: string = this.logManager.getDebugParamValue(DebugURLParams.ZONE_MODIFICATIONS_ADD);
            this.debugLog(`Adding zone modifications through debug parameter to include: ${zoneMods}`, [LogType.CXO, LogType.QA]);
            zoneMods.split('|').forEach(zm => this.groupConfig.zoneModifications.push(zm));
          }
          if(this.logManager.getDebugParamValue(DebugURLParams.FORCE_BRAINLY)?.includes('t') && !this.groupConfig.zoneModifications.includes(ZoneModifications.BRAINLY)){
            this.groupConfig.zoneModifications.push(ZoneModifications.BRAINLY);
          }
          if(this.groupConfig.zoneModifications.includes(ZoneModifications.ALWAYS_ON)){
            this.groupConfig.insertFrequency = 0;
            this.groupConfig.carouselEnabled = false;
            this.groupConfig.persistentInserts = true;
          }
          if(this.logManager.getDebugParamValue(DebugURLParams.PREVENT_BANNER_ENLARGE)?.includes('t') && !this.groupConfig.zoneModifications.includes(ZoneModifications.PREVENT_BANNER_UPSCALE)){
            this.groupConfig.zoneModifications.push(ZoneModifications.PREVENT_BANNER_UPSCALE);
          }
          if(this.logManager.getDebugParamValue(DebugURLParams.REMOVE_LOGO)?.includes('t') && !this.groupConfig.zoneModifications.includes(ZoneModifications.REMOVE_LOGO)){
            this.groupConfig.zoneModifications.push(ZoneModifications.REMOVE_LOGO);
          }
          if(this.logManager.getDebugParamValue(DebugURLParams.DISABLE_INSERT_OPEN_ANIMATION)?.includes('t') && !this.groupConfig.zoneModifications.includes(ZoneModifications.DISABLE_OPEN_ANIMATION)){
            this.groupConfig.zoneModifications.push(ZoneModifications.DISABLE_OPEN_ANIMATION);
          }
          if(this.logManager.getDebugParamValue(DebugURLParams.DISABLE_TAM)?.includes('t') && !this.groupConfig.zoneModifications.includes(ZoneModifications.DISABLE_TAM)){
            this.groupConfig.zoneModifications.push(ZoneModifications.DISABLE_TAM);
          }
          if(this.logManager.getDebugParamValue(DebugURLParams.FORCE_MANUAL_BIDDING)?.includes('t') && !this.groupConfig.zoneModifications.includes(ZoneModifications.MANUAL_BIDDING)){
            this.groupConfig.zoneModifications.push(ZoneModifications.MANUAL_BIDDING);
          }
          if(this.groupConfig.zoneModifications.includes(ZoneModifications.MANUAL_BIDDING)){
            this.debugLog("Manual Bidding Enabled", [LogType.CXO, LogType.QA]);
            this.configureManualBidding();
            this.addManualBiddingEvents();
          }
          
          if (this.groupConfig.zoneModifications.some(mod => [ZoneModifications.BRAINLY, ZoneModifications.HIDE_UI, ZoneModifications.ALWAYS_ON].includes(mod as ZoneModifications)) && !this.groupConfig.zoneModifications.includes(ZoneModifications.REMOVE_INSERT_CLOSE_BUTTON)) {
            this.groupConfig.zoneModifications.push(ZoneModifications.REMOVE_INSERT_CLOSE_BUTTON);
          }
    
          if(this.groupConfig.zoneModifications.length > 0){
            let zoneModLog: string[] = [`Zone Modifications set for zone '${configsGroup}':`];
            this.groupConfig.zoneModifications.forEach(mod => zoneModLog.push(mod));
            this.debugLog(zoneModLog, [LogType.CXO, LogType.QA]);
          }
    
          const analyticsTier:PlayerEventLogFrequency = (this.groupConfig?.analyticsTier?.toUpperCase?.() as PlayerEventLogFrequency);
          if(Object.values(PlayerEventLogFrequency).includes(analyticsTier)){
            this.analyticsEventDispatcher.assignPlayerEventFrequency(analyticsTier);
          }
    
          this.isCTV = (this.groupConfig?.zoneType === ZoneType.CTV)?true:false;
          this.debugLog('Is CTV zone from group config? ' + this.isCTV, [LogType.QA, LogType.CXO]);
          const forceCtvModeParam = this.logManager.getDebugParamValue(DebugURLParams.FORCE_CTV_MODE);
          if(forceCtvModeParam !== null && forceCtvModeParam === 'true'){
            this.isCTV = true;
            this.debugLog('CTV zone forced from URL parameters', [LogType.QA, LogType.CXO]);
          }
    
          if(!this.isCTV){
            this.debugLog(['registering identity hub'], LogType.QA);
            this.registerIdentityHub();
            this.debugLog('Collecting GPP Data', [LogType.QA]);
            this.getGPPData();
            this.debugLog('Collecting TC string', [LogType.QA]);
            this.getTCData();
            this.debugLog('Collecting US Privacy string', [LogType.QA]);
            this.getUSPData();
            this.initializeIntersectionObserver();
            this.attemptPageAnalysis();
          }else{
            this.containerViewabilityValue = 1;
            this.groupConfig.destroyInsertOnClose = true;
          }
          
          /*
          if(isNil(this.osdConfig) && isNil(groupConfigs?.osdConfig)){
            const osdConfigsTest: OSDGroupConfig = {
              adUnits:[
                {adUnitPath: '/21681896075/catapultx/cxo-on-stream'}
              ],
              bidders:[
                {
                    bidder: 'catapultx',
                    params: {
                        groupId: 'internal',
                    }
                }
              ]
            }
            groupConfigs.osdConfig = osdConfigsTest;
          }
          */
          
          const groupOSDConfig: OSDGroupConfig = this.groupConfig?.osdConfig;
    
          const qElementId: string = this.getQElementId();
          if(!isNil(groupOSDConfig) && qElementId !== null){
            const currentElementId: string = (qElementId.charAt(0) === '#' || qElementId.charAt(0) === '.')?qElementId.substring(1):qElementId;
            let foundAdUnit: {[key:string]:{}} = groupOSDConfig.adUnits.find(adUnit => {return (adUnit.adUnitPath !== null && adUnit.adUnitPath !== ""  && adUnit.videoId === currentElementId)?true:false}) ?? groupOSDConfig.adUnits.find(adUnit => {return (adUnit.adUnitPath !== null && adUnit.adUnitPath !== ""  && adUnit.videoId === null)?true:false});
    
            if(!!foundAdUnit) {
              this.debugLog([`Ad Unit found for OSD configuration: ${JSON.stringify(foundAdUnit, null, 2)}`], [LogType.CXO, LogType.QA]);
    
              this.debugLog(['bidderArray = ', groupOSDConfig.bidders], [LogType.CXO, LogType.QA]);
              this.debugLog('Initialize OSD setup from zone configuation', [LogType.CXO, LogType.QA]);
    
    
              this.osdConfig = this.normalizeOSDConfig({
                slotRefreshParams:{
                  gptAdUnitId: groupOSDConfig.adUnits[0].adUnitPath,
                  bidderArray: groupOSDConfig.bidders ?? null
                }
              });
    
              if(this.logManager.getDebugParamValue(DebugURLParams.FORCE_PREBID) !== null){
                this.osdConfig.slotRefreshParams.allowGAMRefresh = false;
              }
              
            } else {
              this.debugLog([`Ad Unit not found within OSD configuration that matches video container ${this.getQElementId()}`], [LogType.CXO, LogType.QA]);
            }
    
          }
    
          const mobileAdSizes: number[][] = this.groupConfig?.mobileAdSizes ?? this.groupConfig?.desktopAdSizes ?? this.groupConfig?.adSizes;
          const desktopAdSizes: number[][] = this.groupConfig?.desktopAdSizes ?? this.groupConfig?.mobileAdSizes ?? this.groupConfig?.adSizes;
          if(desktopAdSizes?.length > 0 || mobileAdSizes?.length > 0){
            let desktopAllowedAdSizes: string = '';
            desktopAdSizes.forEach(sz => {if(desktopAllowedAdSizes.length > 0){desktopAllowedAdSizes += ', '}desktopAllowedAdSizes+=`${sz[0]}x${sz[1]}`});
            let mobileAllowedAdSizes: string = '';
            mobileAdSizes.forEach(sz => {if(mobileAllowedAdSizes.length > 0){mobileAllowedAdSizes += ', '}mobileAllowedAdSizes+=`${sz[0]}x${sz[1]}`});
            this.debugLog(`Will modify allowed OSD ad sizes for group ${this.groupId} using sizes from zone configuration: DESKTOP - ${desktopAllowedAdSizes} | MOBILE - ${mobileAllowedAdSizes}`, [LogType.CXO, LogType.QA]);
            for(let type in this.allowedOnstreamDirectDesktopSizes){
              this.allowedOnstreamDirectDesktopSizes[type] = this.allowedOnstreamDirectDesktopSizes[type].filter(size => desktopAdSizes.find(allowed => String(allowed) === String(size)))
            }
            for(let type in this.allowedOnstreamDirectMobileSizes){
              this.allowedOnstreamDirectMobileSizes[type] = this.allowedOnstreamDirectMobileSizes[type].filter(size => mobileAdSizes.find(allowed => String(allowed) === String(size)))
            }
            this.debugLog(['Updated allowed OSD sizes:', `Desktop`, this.allowedOnstreamDirectDesktopSizes, `Mobile:`, this.allowedOnstreamDirectMobileSizes], [LogType.CXO, LogType.QA]);
          }else if(this.groupConfig.zoneModifications.includes(ZoneModifications.BRAINLY)){
    
            this.debugLog(`Will modify allowed OSD ad sizes for group ${this.groupId}.`, [LogType.CXO, LogType.QA]);
            const allowedDesktopSizes: number[][] = [
              [728, 90],
              [300, 250]
            ]
            const allowedMobileSizes: number[][] = [
              [320, 50],
              [300, 250]
            ]
            for(let type in this.allowedOnstreamDirectDesktopSizes){
              this.allowedOnstreamDirectDesktopSizes[type] = this.allowedOnstreamDirectDesktopSizes[type].filter(size => allowedDesktopSizes.find(allowed => String(allowed) === String(size)))
            }
            for(let type in this.allowedOnstreamDirectMobileSizes){
              this.allowedOnstreamDirectMobileSizes[type] = this.allowedOnstreamDirectMobileSizes[type].filter(size => allowedMobileSizes.find(allowed => String(allowed) === String(size)))
            }
            this.debugLog(['New osd sizes:', this.allowedOnstreamDirectDesktopSizes, this.allowedOnstreamDirectMobileSizes], [LogType.CXO, LogType.QA]);
          }else if(this.groupConfig?.publisherId === 'OSD_SIZES_OVERRIDE__UNUSED'){
    
            this.debugLog(`Will modify allowed OSD ad sizes for group ${this.groupId}.`, [LogType.CXO, LogType.QA]);
            const allowedOSDSizes: number[][] = [
              [234, 60],
              [320, 50],
              [468, 60]
            ]
            this.allowedOnstreamDirectDesktopSizes.squeezeback = [...allowedOSDSizes];
            this.allowedOnstreamDirectDesktopSizes.overlayCorner = [...allowedOSDSizes];
            this.allowedOnstreamDirectDesktopSizes.flap = [...allowedOSDSizes];
            this.allowedOnstreamDirectDesktopSizes.overlayFullwidth = [...allowedOSDSizes];
            this.allowedOnstreamDirectMobileSizes.squeezeback = [...allowedOSDSizes];
            this.allowedOnstreamDirectMobileSizes.overlayCorner = [...allowedOSDSizes];
            this.allowedOnstreamDirectMobileSizes.flap = [...allowedOSDSizes];
            this.allowedOnstreamDirectMobileSizes.overlayFullwidth = [...allowedOSDSizes];
            
            this.debugLog(['New osd sizes:', this.allowedOnstreamDirectDesktopSizes, this.allowedOnstreamDirectMobileSizes], [LogType.CXO, LogType.QA]);
          }
    
          if(!isNil(this.groupConfig?.groupId)){
            if(isNil(this.groupId)){
              this.groupId = this.groupConfig.groupId;
              this.analyticsEventDispatcher.assignEventsGroupId(this.groupId);
              this.debugLog(`No group id defined at ${this.componentElement?.tagName} start. Group id '${this.groupId}' found in group configs lookup.`, [LogType.CXO, LogType.QA]);
            }else if(this.groupId !== this.groupConfig.groupId){
              this.debugLog(`Group id defined at ${this.componentElement?.tagName} = ${this.groupId} and group id found in zone configs = ${this.groupConfig.groupId}. GroupId mismatch debug event will be sent.`, [LogType.CXO, LogType.QA]);
              const debugData: {} = {
                definedGroupId: this.groupId,
                domainGroupId: this.groupConfig.groupId
              }
              this.analyticsEventDispatcher.sendOverlayEvent(AnalyticsEventType.OVERLAY_GROUPID_ZONE_MISMATCH, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), this.elapsedOverlayTime, this.activeOverlayTime, this.containerViewabilityValue, debugData);
            }
          }else if(isNil(this.groupId)){
            this.debugLog(`No group id defined at ${this.componentElement?.tagName} start and no groupid defined in cx configuration. Shutting down ${this.componentElement?.tagName}.`, [LogType.CXO, LogType.QA]);
            this.destroyFunction();
            const debugData: {} = {
              definedGroupId: null,
              domainGroupId: null
            }
            this.analyticsEventDispatcher.sendOverlayEvent(AnalyticsEventType.OVERLAY_GROUPID_NOT_FOUND, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), this.elapsedOverlayTime, this.activeOverlayTime, this.containerViewabilityValue, debugData);
          }else{
            this.debugLog(`Group id defined at ${this.componentElement?.tagName} ${this.groupId} and no group id found in zone configs. GroupId mismatch debug event will be sent.`, [LogType.CXO, LogType.QA, LogType.IMPORTANT]);
            const debugData: {} = {
              definedGroupId: this.groupId,
              domainGroupId: null
            }
            this.analyticsEventDispatcher.sendOverlayEvent(AnalyticsEventType.OVERLAY_GROUPID_ZONE_MISMATCH, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), this.elapsedOverlayTime, this.activeOverlayTime, this.containerViewabilityValue, debugData);
          }
    
          if(overrideConfigsGroupIdParam !== null && configsGroup !== this.groupId){
            this.debugLog(`Set group-id of '${this.groupId}' is not the same as override group-id '${configsGroup}'. Removing all ADX settings.`, [LogType.QA, LogType.CXO]);
            this.groupConfig.gptAdUnitPageUrl = null;
            this.groupConfig.gptAdUnitPath = null;
          }
          const overrideOnboardingParam = this.logManager.getDebugParamValue(DebugURLParams.ONBOARDING);
          if(overrideOnboardingParam !== null){
            const overrideOnboarding: boolean = (overrideOnboardingParam.indexOf('f')>=0)?false:true;
            this.groupConfig.onboarding = overrideOnboarding;
            this.debugLog(`Forcing zone property "onboarding" to ${overrideOnboarding}`, [LogType.QA, LogType.CXO]);
          }
          const persistentInsertParam = this.logManager.getDebugParamValue(DebugURLParams.PERSISTENT_INSERTS);
          if(persistentInsertParam !== null){
            const persistentInserts: boolean = (persistentInsertParam.indexOf('t')>=0)?true:false;
            this.groupConfig.persistentInserts = persistentInserts;
            this.debugLog(`Forcing zone property "persistentInserts" to ${persistentInserts}`, [LogType.QA, LogType.CXO]);
          }
          const allowAdXParam = this.logManager.getDebugParamValue(DebugURLParams.ALLOW_ADX);
          if(allowAdXParam !== null){
            const allowADX: boolean = (allowAdXParam.indexOf('t')>=0)?true:false;
            if(!allowADX){
              this.debugLog(`DEBUGallowADX=FALSE. Disallowing ADX to run for zone.`, [LogType.QA, LogType.CXO]);
              this.groupConfig.gptAdUnitPageUrl = null;
              this.groupConfig.gptAdUnitPath = null;
            }
          }
          const carouselModeParam = this.logManager.getDebugParamValue(DebugURLParams.CAROUSEL_MODE);
          if(carouselModeParam !== null){
            const carouselMode: boolean = (carouselModeParam.indexOf('t')>=0)?true:false;
            this.groupConfig.carouselEnabled = carouselMode;
            this.groupConfig.carouselSpeed = 7;
            this.debugLog('Forcing zone property "carouselMode" to ' + carouselMode, [LogType.QA, LogType.CXO]);
          }
          const destroyInsertOnCloseParam = this.logManager.getDebugParamValue(DebugURLParams.DESTROY_INSERT_ON_CLOSE);
          if(destroyInsertOnCloseParam !== null){
            const allowReopen: boolean = (destroyInsertOnCloseParam.indexOf('t')>=0)?true:false;
            this.groupConfig.destroyInsertOnClose = allowReopen;
            this.debugLog('Forcing zone property "destroyInsertOnClose" to ' + allowReopen, [LogType.QA, LogType.CXO]);
          }
          const carouselSpeedParam = this.logManager.getDebugParamValue(DebugURLParams.CAROUSEL_SPEED);
          if(carouselSpeedParam !== null){
            const parsedValue = parseInt(carouselSpeedParam);
            this.groupConfig.carouselSpeed = Number.isInteger(parsedValue) ? parsedValue : 7;
            this.debugLog('Forcing zone property "carouselSpeed" to ' + this.groupConfig.carouselSpeed, [LogType.QA, LogType.CXO]);
          }
          const insertFrequencyParam = this.logManager.getDebugParamValue(DebugURLParams.INSERT_FREQUENCY);
          if(insertFrequencyParam !== null){
            const parsedValue = parseInt(insertFrequencyParam);
            this.groupConfig.insertFrequency = Number.isInteger(parsedValue) ? parsedValue : 150;
            this.debugLog('Forcing zone property "insertFrequency" to ' + this.groupConfig.insertFrequency, [LogType.QA, LogType.CXO]);
          }
          const insertDurationParam = this.logManager.getDebugParamValue(DebugURLParams.INSERT_DURATION);
          if(insertDurationParam !== null){
            const parsedValue = parseInt(insertDurationParam)
            this.groupConfig.insertDuration = Number.isInteger(parsedValue) ? parsedValue : 150;
            this.debugLog('Forcing zone property "insertDuration" to ' + this.groupConfig.insertDuration, [LogType.QA, LogType.CXO]);
          }
          const brandBugParam = this.logManager.getDebugParamValue(DebugURLParams.BRAND_BUG);
          if(brandBugParam !== null){
            const brandBug: boolean = (brandBugParam === 'false')?false:true;
            this.groupConfig.brandBug = brandBug;
            this.debugLog('Forcing zone property "brandBug" to ' + brandBug, [LogType.QA, LogType.CXO]);
          }
          const zIndexOverrideParam = this.logManager.getDebugParamValue(DebugURLParams.Z_INDEX_OVERRIDE);
          if(zIndexOverrideParam !== null){
            const parsedValue = parseInt(zIndexOverrideParam);
            this.groupConfig.zIndexOverride = Number.isInteger(parsedValue) ? parsedValue : 10000000;
            this.debugLog('Forcing zone property "zIndexOverride" to ' + this.groupConfig.zIndexOverride, [LogType.QA, LogType.CXO]);
          }
          const zoneActiveParam = this.logManager.getDebugParamValue(DebugURLParams.ZONE_ACTIVE);
          if(zoneActiveParam !== null){
            const zoneActive: boolean = (zoneActiveParam === 'false')?false:true;
            this.groupConfig.active = zoneActive;
            this.debugLog('Forcing zone property "active" to ' + zoneActive, [LogType.QA, LogType.CXO]);
          }
          const externalLogging:string = this.logManager.getDebugParamValue(DebugURLParams.EXTERNAL_LOGGING);
          if(externalLogging !== null){
            const externalLoggingFlag: boolean = (externalLogging.toLowerCase().includes('t'))?true:false;
            this.groupConfig.externalLogging = externalLoggingFlag;
            this.debugLog('Forcing zone property "externalLogging" to ' + externalLoggingFlag, [LogType.QA, LogType.CXO]);
          }
          if(Boolean(this.groupConfig?.externalLogging) === true){
            this.logManager.toggleRemoteLogging = true;
            this.debugLog("`externalLogging` detected on the Group Config, setting toggleRemoteLogging to true", [LogType.QA, LogType.CXO])
          }
    
          this.groupConfig.active ??= true;
          
          this.groupConfig.persistentInserts ??= false;
          if(this.groupConfig.persistentInserts){
            this.debugLog(`persistentInserts turned ON for zone '${this.groupConfig.zoneId}' .`, [LogType.QA, LogType.CXO]);
          }
    
          this.groupConfig.onboarding ??= false;
          if(this.groupConfig.onboarding){
            this.debugLog([`Zone 'onboading' = '${this.groupConfig.onboarding}'. Will shutdown overlay.`], [LogType.CXO, LogType.QA]);
            this.destroyFunction();
          }
    
          this.timerBidding((this.groupConfig.persistentInserts || this.componentName === ComponentSourceType.SLIDESHOW)?true:false);
    
          const timerBaseBiddingParam = this.logManager.getDebugParamValue(DebugURLParams.TIMER_BASED_BIDDING);
          if(timerBaseBiddingParam !== null){
            const timerBidding: boolean = (timerBaseBiddingParam.includes('t'))?true:false;
            this.timerBidding(timerBidding);
            this.debugLog(`Forcing timer based bidding to be '${timerBidding}'`, [LogType.QA, LogType.CXO]);
          }
    
          if(this.groupConfig.bidOrder?.length > 0) {
            const bidOrderFromConfig = this.groupConfig.bidOrder.filter(x => Object.values(BidOriginSourceType).includes(x as BidOriginSourceType));
            this.bidderOrder = bidOrderFromConfig;
            this.debugLog(`Assigning bidderOrder from Group Configs: ${bidOrderFromConfig}`, [LogType.QA, LogType.CXO]);
          }
    
          if(!this.groupConfig.revContentWidgetId){
            this.bidderOrder = this.bidderOrder.filter(bidder => bidder !== BidOriginSourceType.REV_CONTENT);
          }
    
          this.groupConfig.outbrain ??= false;
          if(!this.groupConfig.outbrain){
            this.bidderOrder = this.bidderOrder.filter(bidder => bidder !== BidOriginSourceType.OUTBRAIN);
          }
    
          if(!this.isADXAvailable){
            this.bidderOrder = this.bidderOrder.filter(bidder => bidder !== BidOriginSourceType.ADX);
          }
          
          this.groupConfig.destroyInsertOnClose ??= false;
          if(!this.groupConfig.destroyInsertOnClose){
            this.debugLog(`destroyInsertOnClose turned OFF for zone ${this.groupConfig.zoneId}' .`, [LogType.QA, LogType.CXO]);
          }
    
          if(!isNil(this.groupConfig.zIndexOverride)){
            this.zIndexUpdate(String(this.groupConfig.zIndexOverride));
          }
    
          if(!isNil(this.groupConfig.regionInfo)){
            var locationData: UserRegionInfo = {
              country: this.groupConfig.regionInfo.country ?? null,
              region: this.groupConfig.regionInfo.region ?? null,
              regionCode: this.groupConfig.regionInfo.regionCode ?? null,
              city: this.groupConfig.regionInfo.city ?? null,
              zip: this.groupConfig.regionInfo.zip ?? null,
              latitude: this.groupConfig.regionInfo.latitude ?? null,
              longitude: this.groupConfig.regionInfo.longitude ?? null
            }
            this.analyticsEventDispatcher.assignEventsLocationData(locationData);
          }
    
          if(!isNil(this.groupConfig.deviceInfo)){
            var deviceData: UserDeviceInfo = {
              make: this.groupConfig.deviceInfo.make ?? null,
              model: this.groupConfig.deviceInfo.model ?? null,
              os: this.groupConfig.deviceInfo.os ?? null,
              osVersion: this.groupConfig.deviceInfo.osVersion ?? null,
              browser: this.groupConfig.deviceInfo.browser ?? null,
              browserVersion: this.groupConfig.deviceInfo.browserVersion ?? null
            }
            this.analyticsEventDispatcher.assignEventsDeviceData(deviceData);
          }
    
          if(this.isBlockedURL){
            this.debugLog('Current URL is part of blocked URLs for requested zone.', [LogType.QA, LogType.CXO]);
          }
    
          this.debugLog([`Player configurations: `, this.groupConfig.globalPlayerConfigs], [LogType.QA, LogType.CXO]);
    
          const serverConfigs: string = JSON.stringify(this.groupConfig?.insertConfig);
          this.debugLog([`Allowed insert configurations from server: `, JSON.parse(serverConfigs)], [LogType.QA, LogType.CXO]);
    
          if(!isNil(this.groupConfig?.insertConfig)){
            Object.keys(this.groupConfig?.insertConfig).forEach(playerMode => {
              this.groupConfig.insertConfig[playerMode] ??= [];
              if(this.isCTV){
                this.groupConfig.insertConfig[playerMode] = (this.groupConfig.insertConfig[playerMode].length === 0 && this.groupConfig.insertConfig?.allowedInsertsCtvOnStream?.length > 0)?this.groupConfig.insertConfig.allowedInsertsCtvOnStream:[];
              }
      
              const allowedInsertTypesAddParam = this.logManager.getDebugParamValue(DebugURLParams.ALLOWED_INSERT_TYPES_ADD);
              if(allowedInsertTypesAddParam !== null){
                const addInserts: string[] = allowedInsertTypesAddParam.split('_');
                this.debugLog('URL Params insert types to add to allowed inserts: ' + addInserts, [LogType.QA, LogType.CXO]);
                for(let i = 0; i < addInserts.length; i++){
                  this.groupConfig.insertConfig[playerMode].push({'insertType':addInserts[i], 'overrides':null});
                }
              }
      
              const allowedInsertTypesForceParam = this.logManager.getDebugParamValue(DebugURLParams.ALLOWED_INSERT_TYPES_FORCE);
              if(allowedInsertTypesForceParam !== null){
                const forceInserts: string[] = allowedInsertTypesForceParam.split('_');
                this.debugLog([`Debug params forced allowed insert types for ${playerMode}: ${String(forceInserts)}`], [LogType.QA, LogType.CXO]);
                const addInserts: AllowedInsert[] = [];
                forceInserts.forEach(ins =>{
                  const forcedInsert: AllowedInsert = this.groupConfig.insertConfig[playerMode].find(existingInsert => existingInsert.insertType === ins) ?? {insertType:ins as InsertType, overrides:null};
                  addInserts.push(forcedInsert);
                })
                this.groupConfig.insertConfig[playerMode] = [];
                addInserts.forEach(ins => this.groupConfig.insertConfig[playerMode].push(ins));
              }
      
              if(playerMode.includes('InArticle') || playerMode.includes('OnStream')){
                const allowedInsertTypesOnstreamForceParam = this.logManager.getDebugParamValue(DebugURLParams.ALLOWED_INSERT_TYPES_ONSTREAM_FORCE);
                if(allowedInsertTypesOnstreamForceParam !== null){
                  const forceInserts: string[] = allowedInsertTypesOnstreamForceParam.split('_');
                  this.debugLog([`Debug params forced allowed insert types for ${playerMode}: ${String(forceInserts)}`], [LogType.QA, LogType.TAM, LogType.CXO]);
                  const addInserts: AllowedInsert[] = [];
                  forceInserts.forEach(ins =>{
                    const forcedInsert: AllowedInsert = this.groupConfig.insertConfig[playerMode].find(existingInsert => existingInsert.insertType === ins) ?? {insertType:ins as InsertType, overrides:null};
                    addInserts.push(forcedInsert);
                  })
                  this.groupConfig.insertConfig[playerMode] = [];
                  addInserts.forEach(ins => this.groupConfig.insertConfig[playerMode].push(ins));
                }
              }
      
              if(playerMode.includes('Floating')){
                const allowedInsertTypesFloatingForceParam = this.logManager.getDebugParamValue(DebugURLParams.ALLOWED_INSERT_TYPES_FLOATING_FORCE);
                if(allowedInsertTypesFloatingForceParam !== null){
                  const forceInserts: string[] = allowedInsertTypesFloatingForceParam.split('_');
                  this.debugLog([`Debug params forced allowed insert types for ${playerMode}: ${String(forceInserts)}`], [LogType.QA, LogType.TAM, LogType.CXO]);
                  const addInserts: AllowedInsert[] = [];
                  forceInserts.forEach(ins =>{
                    const forcedInsert: AllowedInsert = this.groupConfig.insertConfig[playerMode].find(existingInsert => existingInsert.insertType === ins) ?? {insertType:ins as InsertType, overrides:null};
                    addInserts.push(forcedInsert);
                  })
                  this.groupConfig.insertConfig[playerMode] = [];
                  addInserts.forEach(ins => this.groupConfig.insertConfig[playerMode].push(ins));
                }
              }
      
              this.groupConfig.insertConfig[playerMode].forEach((iType: AllowedInsert) => {
                this.defaultInsertTypeMapping.forEach(insertMapping =>{
                  if(insertMapping.defaultType === iType.insertType){
                    iType.insertType = insertMapping.mappedTypes[0];
                    for(let i = 1; i < insertMapping.mappedTypes.length; i++){
                      this.groupConfig.insertConfig[playerMode].push({insertType: insertMapping.mappedTypes[i], overrides:iType.overrides});
                    }
                  }
                })
      
                if(this.isCTV && this.groupConfig.insertConfig.allowedInsertsCtvOnStream !== null && this.groupConfig.insertConfig.allowedInsertsCtvOnStream.length > 0){
                  this.debugLog([`Setting insert configuration for CTV ${playerMode} equal to CTVOnStream insert configuration.`, this.groupConfig.insertConfig.allowedInsertsCtvOnStream], [LogType.CXO, LogType.QA]);
                  this.groupConfig.insertConfig[playerMode] = [...this.groupConfig.insertConfig.allowedInsertsCtvOnStream];
                }
      
                const insertOverridePositionParam = this.logManager.getDebugParamValue(DebugURLParams.INSERT_OVERRIDE_POSITION);
                if(insertOverridePositionParam !== null){
                  const overridePosition: string = insertOverridePositionParam;
                  if(isNil(iType.overrides)){ iType.overrides = []; }
                  const overridePositionObject = iType.overrides.find(x => x.key.toLowerCase() === InsertOverrideType.POSITION.toLowerCase());
                  if(!isNil(overridePositionObject)){
                    this.debugLog('override position object: ' + overridePositionObject, [LogType.CXO, LogType.QA]);
                    overridePositionObject.value = overridePosition;
                  }else{
                    iType.overrides.push({'key':InsertOverrideType.POSITION, 'value':overridePosition});
                  }
                }
                const overrideYOffsetParam = this.logManager.getDebugParamValue(DebugURLParams.INSERT_OVERRIDE_Y_OFFSET);
                if(overrideYOffsetParam !== null){
                  const overrideYOffset: string = overrideYOffsetParam;
                  if(isNil(iType.overrides)){ iType.overrides = []; }
                  const overrideYOffsetObject = iType.overrides.find(x => x.key.toLowerCase() === InsertOverrideType.YOFFSET.toLowerCase());
                  if(!isNil(overrideYOffsetObject)){
                    this.debugLog('override position object: ' + overrideYOffsetObject, [LogType.CXO, LogType.QA]);
                    overrideYOffsetObject.value = overrideYOffset;
                  }else{
                    iType.overrides.push({'key':InsertOverrideType.YOFFSET, 'value':overrideYOffset});
                  }
                }
              })
    
              const allowedInsertTypesRemoveParam = this.logManager.getDebugParamValue(DebugURLParams.ALLOWED_INSERT_TYPES_REMOVE);
              if(allowedInsertTypesRemoveParam !== null){
                const removeInserts: string[] = allowedInsertTypesRemoveParam.split('_');
                this.debugLog('URL Params remove allowed insert types: ' + removeInserts, [LogType.QA, LogType.CXO]);
                this.groupConfig.insertConfig[playerMode] = this.groupConfig.insertConfig[playerMode].filter((iType: AllowedInsert) => !removeInserts.includes(iType.insertType.toLowerCase()));
              }
      
              if(!this.isCTV){
                this.groupConfig.insertConfig[playerMode] = this.groupConfig.insertConfig[playerMode].filter((iType: AllowedInsert) => {
                  const filterCTV: boolean = !(iType.insertType === this.currentInserts.ctvOverlayCorner || iType.insertType === this.currentInserts.ctvSqueeze);
                  if(!filterCTV){
                    this.debugLog(`Filter out CTV insert ${iType.insertType} from available inserts for ${playerMode}`, LogType.CXO);
                  }
                  return filterCTV
                });
              }else{
                this.groupConfig.insertConfig[playerMode] = this.groupConfig.insertConfig[playerMode].filter((iType: AllowedInsert) => {
                  const filterCTV: boolean = !(iType.insertType !== this.currentInserts.ctvOverlayCorner && iType.insertType !== this.currentInserts.ctvSqueeze);
                  if(!filterCTV){
                    this.debugLog(`Filter out NON-CTV insert ${iType.insertType} from available inserts for ${playerMode}`, LogType.CXO);
                  }
                  return filterCTV
                });
              }
      
            })
          }
          
          this.debugLog([`Allowed insert configurations after overrides and filters: `, this.groupConfig.insertConfig], [LogType.QA, LogType.CXO]);
        }

        this.groupConfigsProcessed = true;
    }

    initiateCarouselBid(){
        const params: BidLogicParameters = this.getBidLogicParams();
        const carouselTimeSinceStart: number = this.carouselBidTimer.getTotalElapsedTime()/1000;
        const lastAllowedBidTime: number = (this.groupConfig.insertDuration+3)-this.groupConfig.carouselSpeed
        if(carouselTimeSinceStart < lastAllowedBidTime){
          if(isNil(params.carouselResource) && !this.activeCarouselData.isClosed){
            this.debugLog(`Initiating carousel bid #${++this.activeCarouselData.bidCount} at ${carouselTimeSinceStart} seconds after carousel start.`, [LogType.CXO, LogType.QA]);
            if(this.isOverlay){
                if((!isNil(params.videoElement) && !params.videoElement.paused) || this.groupConfig.persistentInserts === true){
                    this.initiateBidRequestTimer.execute();
                  }else{
                    this.debugLog([`Video paused during carousel bid request attempt (likely mid-roll). Will not make bid request.`], [LogType.CXO, LogType.QA]);
                  }
            }else{
                this.initiateBidRequestTimer.execute();
            }
            this.debugLog([`Attempting new carousel bid in ${(this.groupConfig.carouselSpeed)} seconds.`], [LogType.CXO, LogType.QA]);
            this.carouselBidTimer.reset(this.groupConfig.carouselSpeed*1000);
          }else{
            if(this.activeCarouselData.isClosed){
              if(this.groupConfig.insertDuration > 0){
                this.debugLog([`Carousel has been closed. Will not initiate a new bid request. Attempting new carousel bid in 1 second.`], [LogType.CXO, LogType.QA]);
                this.carouselBidTimer.reset(1000);
              }
            }else{
              this.debugLog([`Carousel insert is already loaded and awaiting display.`, params.carouselResource,  `Will not initiate a new bid request. Attempting new carousel bid in 1 second.`], [LogType.CXO, LogType.QA]);
              this.carouselBidTimer.reset(1000);
            }
          }
        }else{
          if(this.groupConfig.insertDuration > 0){
            this.debugLog(`${carouselTimeSinceStart} seconds elapsed since carousel start. Last chance for carousel bid ended at ${lastAllowedBidTime} seconds. Will not make further bid requests.`, [LogType.CXO, LogType.QA])
          }
          this.resetCarouselData();
        }
      }

    resetCarouselData(){
        this.activeCarouselData.bidCount = 0;
        this.activeCarouselData.active = false;
        this.activeCarouselData.isClosed = false;
        this.activeCarouselData.isCompleted = true;
        this.carouselBidTimer.cancel();
        this.carouselBidTimer = new CustomTimeout(() => { this.initiateCarouselBid() }, 0, 'Carousel Bid Timer', this.logManager, LogType.CXO)
    }

    initiateBidRequest(){
        const params: BidLogicParameters = this.getBidLogicParams();
        const isBidTimeAllowed: boolean = this.isBidTimeAllowed;
        const isBlockedURL: boolean = this.isBlockedURL;
        const isVideoOnPage: boolean = params.isVideoOnPage;
        const isFullscreenIOS: boolean = this.isFullscreenIOS;
        const isFullscreenVideoNode: boolean = params.isFullscreenVideoNode;
    
        params.updateCXO();
    
        let documentHidden: boolean = document.hidden;
        documentHidden = false;
    
        if((this.forceNextManualBid || (this.forceNextBid && this.isBidTimeAllowed) || this.groupConfig.persistentInserts || (!isNil(params.videoElement) && isVideoOnPage && isBidTimeAllowed && this.currentDimensions.width > 10 && this.currentDimensions.height > 10)) 
        && (this.groupConfig?.active === true && !isFullscreenVideoNode && !isFullscreenIOS && this.getCXOActive() && !documentHidden && !params.cxWait && !params.nobid && !isBlockedURL && isNil(params.stowedBidResponse) && (this.forceNextBid || (isNil(this.getActiveResourceData()) || isNil(this.getCarouselResourceData()))))){
          this.debugLog(`Force next bid? ${this.forceNextBid}`, LogType.CXO);
          this.forceNextRebid = this.forceNextBid;
          this.forceNextBid = false;
          this.forceNextManualBid = false;
    
          let allowRebid: boolean = true;
          if(this.foundUID === "NOT_FOUND"){
            const allowRebidsParam = this.logManager.getDebugParamValue(DebugURLParams.ALLOW_REBIDS);
            if(allowRebidsParam !== null && allowRebidsParam === 'true'){
              this.debugLog(`UID NOT FOUND, but DEBUGallowRebids=true. Will continue to allow rebidding.`, [LogType.QA, LogType.CXO]);
            }else{
              //allowRebid = false;
              this.debugLog(`UID NOT FOUND. Will continue to allow rebidding without UID.`, [LogType.QA, LogType.CXO]);
            }
          }
    
          this.bidCount++;
          
          if(this.rebidCount === 0){
            this.requestedBannerSizes = [];
            this.updateAdOpportunityId();
          }
          
          params.cancelRemakeADXInsertTimer();
    
          const placementType = (this.isOnStream)?1:(this.currentDimensions.isFloating)?3:2;
          const placementString = (placementType===1)?'OnStream':(placementType===2)?'InArticle':'Floating';
    
          let bidGroup: string = this.groupId;
          const overrideGroupIdParam: string = this.logManager.getDebugParamValue(DebugURLParams.OVERRIDE_GROUP_ID);
          if(overrideGroupIdParam !== null){
            bidGroup = (overrideGroupIdParam == null || overrideGroupIdParam === '' || overrideGroupIdParam === 'true')?'internal':overrideGroupIdParam;
            this.debugLog(`Using group-id '${bidGroup}' for bid requests.`, [LogType.QA, LogType.CXO]);
          }
          
          if (isNil(this.gppString)) {
            this.getGPPData(true);
          }
          
          if (isNil(this.tcString)) {
            this.getTCData(true);
          }
          
          if (isNil(this.uspString)) {
            this.getUSPData(true);
          }
    
          const rebidString: string = (this.rebidCount>0)?` (rebid ${this.rebidCount})`:'';
          const useOSDBidSource: boolean = this.isOSDAvailable && !(this.impressionCount < 0 && this.bidCount > this.allowedUnsuccessfulBids);
          const currentBidOrderSource = useOSDBidSource 
            ? 'OSD' + (this.osdBidOrder?.length > 0 
                ? ' - ' + this.osdBidOrder[this.rebidCount] 
                : '') 
            : this.bidderOrder[this.rebidCount];
          this.debugLog(`Attempting bid request ${this.bidCount}${rebidString} for group-id '${bidGroup}'.\n  Bid source: ${currentBidOrderSource}\n  Placement type '${placementString}'.`, [LogType.QA, LogType.CXO]);
    
          
          if(this.logManager.getDebugParamValue(DebugURLParams.FORCE_ADX) !== null){
            this.debugLog('DEBUGforceADX detected. Skipping bid request and forcing ADX banner creation.', [LogType.CXO, LogType.QA]);
            params.generateADXInsert();
          } else if(this.logManager.getDebugParamValue(DebugURLParams.FAKE_NATIVE_BID) !== null){
            this.debugLog('DEBUGfakeNativeBid detected. Skipping bid request and forcing native bid response.', [LogType.CXO, LogType.QA]);
            params.generateNativeInsert();
          }else if(this.isOSDAvailable){
            //if(this.impressionCount === 0 && this.bidCount > this.allowedUnsuccessfulBids){
            if(this.impressionCount < 0 && this.bidCount > this.allowedUnsuccessfulBids){
              this.debugLog(`Will not attempt bid ${this.bidCount}. Too many unsuccessful bid attempts (${this.allowedUnsuccessfulBids} allowed). Shutting down overlay.`, [LogType.QA, LogType.CXO]);
              this.shutdownComponent(`No successful OSD bid responses after ${this.allowedUnsuccessfulBids} bid attempts.`);
            }else{
              params.generateOSDInsert();
            }
          }else if(this.bidderOrder[this.rebidCount] === BidOriginSourceType.ADX){
            this.debugLog(`Attempting ADX ad for bid ${this.bidCount}.`, [LogType.CXO, LogType.QA]);
            params.generateADXInsert();
          }else{
            if(this.rebidCount > 0 && !allowRebid){
              this.debugLog(`UID NOT FOUND. Will not allow rebid attempts.`, [LogType.QA, LogType.CXO]);
              this.attemptRebid(1000, params.rebidBannerSizeAllowedDeviation);
            }else{
              const selectedBanner = params.preferredBannerSize;
              this.debugLog(`Banner requested: '${selectedBanner.insertType}' - ${selectedBanner.bannerSize[0]}x${selectedBanner.bannerSize[1]}.[LogType.QA, LogType.CXO]`)
              const bidParams: ResourceBidParameters = {
                isOutbrainBid: false,
                isRevContentBid: false,
                groupId: bidGroup,
                videoId: params.activeMediaId ?? null,
                placementTypeId: placementType,
                bannerSize: `${selectedBanner.bannerSize[0]}x${selectedBanner.bannerSize[1]}`,
                nativeSize: null,
                uid: this.foundUID,
                eidsIdentityHub: this.eidsIdentityHub ?? null,
                eidsIntentIQ: this.eidsintentIQ ?? null,
                gppString: this.gppString ?? null,
                gppSids: this.gppSids ?? null,
                tcString: this.tcString ?? null,
                gdprApplies: this.gdprApplies,
                uspString: this.uspString ?? null,
                sessionId: this.analyticsSessionId,
                bidId: this.bidRequestId,
                mediaId: params.activeMediaId ?? null,
                logicalVideoId: params.activeLogicalVideoId ?? null,
                startMilliseconds: Math.round(1000*((params.videoElement?.currentTime || 0) + 5)) ?? 0,
                endMilliseconds: Math.round(1000*((params.videoElement?.currentTime || 0) + 20)) ?? 0,
                marketplaceId: params.marketplaceId,
                storeId: params.storeId,
                bundleId: params.bundleId
              }
              
              this.requestedBannerSizes.push(selectedBanner.bannerSize);
    
              const bidTimeStart: number = (new Date()).getTime();
              bidParams.isOutbrainBid = (this.bidderOrder[this.rebidCount] === BidOriginSourceType.OUTBRAIN && this.groupConfig.outbrain)?true:false;
              bidParams.isRevContentBid = (!bidParams.isOutbrainBid && this.bidderOrder[this.rebidCount] === BidOriginSourceType.REV_CONTENT && this.groupConfig.revContentWidgetId != null)?true:false;
              
              const forceRevContentParam = this.logManager.getDebugParamValue(DebugURLParams.FORCE_REV_CONTENT)
              if(forceRevContentParam !== null && forceRevContentParam === 'true'){
                this.debugLog('DEBUGforceRevContent detected. Forcing Rev Content bid.', [LogType.CXO, LogType.QA]);
                bidParams.isRevContentBid = true;
              }
              const forceOutbrainParam = this.logManager.getDebugParamValue(DebugURLParams.FORCE_OUTBRAIN);
              if(forceOutbrainParam !== null && forceOutbrainParam === 'true'){
                this.debugLog('DEBUGforceOutbrain detected. Forcing Outbrain bid.', [LogType.CXO, LogType.QA]);
                bidParams.isOutbrainBid = true;
              }
    
              this.updateBidRequestId();
    
              const bidSource: BidOriginSourceType = (bidParams.isRevContentBid)?BidOriginSourceType.REV_CONTENT:(bidParams.isOutbrainBid)?BidOriginSourceType.OUTBRAIN:BidOriginSourceType.ADK;
    
              const bidData:BidData = {
                overlayTime: this.elapsedOverlayTime/1000,
                activeOverlayTime: this.activeOverlayTime/1000,
                totalBidsCount: this.bidCount,
                adOpportunityBidCount: (this.rebidCount + 1),
                adOpportunityId: this.adOpportunityId,
                bidRequestId: this.bidRequestId,
                nativeImageSizeRequested: 'DEFAULT',
                componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                containerViewability: this.containerViewabilityValue,
                videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0
              }
              if(!bidParams.isRevContentBid && !bidParams.isOutbrainBid){
                bidData.bannerSizeRequested = `${selectedBanner?.bannerSize[0]}x${selectedBanner?.bannerSize[1]}`
              }
              this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_REQUEST, bidSource, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
    
              if(bidParams.isOutbrainBid){
                bidParams.idx = this.outbrainBidCount;
                this.outbrainBidCount++;
                bidParams.outbrainToken = this.outbrainToken;
              }
              fetchResourceData(bidParams).then((resourceData) => {
                this.debugLog([`Full bid response (in ${(new Date()).getTime()-bidTimeStart}ms):`, JSON.stringify(resourceData)], [LogType.CXO]);
                resourceData.isRevContentBid = bidParams.isRevContentBid;
                resourceData.isOutbrainBid = bidParams.isOutbrainBid;
                params.handleResourceResponse(resourceData);
              })
            }
          }
        }else if(params.cxWait){
          this.debugLog('Bid request not initiated because cx-wait attribute has been detected.', [LogType.QA, LogType.CXO]);
          this.cxWaitInitiateBid = true;
          const debugData: {[key:string]:any} = {
            message: 'CXWAIT_TRUE'
          }
          const bidData:BidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            nativeImageSizeRequested: 'DEFAULT',
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue,
            videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0,
            debug: debugData
          }
          this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_NO_REQUEST, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
        }else if(this.groupConfig?.active !== true){
          this.debugLog('Bid request not initiated because zone is not active.', [LogType.QA, LogType.CXO]);
          const debugData: {[key:string]:any} = {
            message: 'ZONE_INACTIVE'
          }
          const bidData:BidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            nativeImageSizeRequested: 'DEFAULT',
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue,
            videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0,
            debug: debugData
          }
          this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_NO_REQUEST, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
        }else if(documentHidden){
          this.debugLog('Bid request not initiated because tab is inactive.', [LogType.QA, LogType.CXO]);
          const debugData: {[key:string]:any} = {
            message: 'TAB_INACTIVE'
          }
          const bidData:BidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            nativeImageSizeRequested: 'DEFAULT',
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue,
            videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0,
            debug: debugData
          }
          this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_NO_REQUEST, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
        }else if(isNil(params.videoElement) || !isVideoOnPage){
          this.debugLog('Bid request not initiated because no existing video player or video player is not on the page.', [LogType.CXO, LogType.QA]);
          const debugData: {[key:string]:any} = {
            message: 'NO_VIDEO_PLAYER'
          }
          const bidData:BidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            nativeImageSizeRequested: 'DEFAULT',
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue,
            videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0,
            debug: debugData
          }
          this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_NO_REQUEST, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
        }else if(!isBidTimeAllowed){
          this.debugLog(`Bid request not initiated because video's current time is within a blocked time range or too close to end of video.`, [LogType.CXO, LogType.QA]);
          const debugData: {[key:string]:any} = {
            message: 'BLOCKED_TIME'
          }
          const bidData:BidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            nativeImageSizeRequested: 'DEFAULT',
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue,
            videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0,
            debug: debugData
          }
          this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_NO_REQUEST, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
        }else if(isBlockedURL){
          this.debugLog(`Bid request not initiated because current URL is defined in the group configuration's blocked URL list.`, [LogType.CXO, LogType.QA]);
          const debugData: {[key:string]:any} = {
            message: 'BLOCKED_URL'
          }
          const bidData:BidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            nativeImageSizeRequested: 'DEFAULT',
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue,
            videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0,
            debug: debugData
          }
          this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_NO_REQUEST, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
        }else if(isFullscreenIOS){
          this.debugLog('iOS detected and video is in native fullscreen - Will not initiate a bid request because no inserts can be displayed.', [LogType.CXO, LogType.QA]);
          const debugData: {[key:string]:any} = {
            message: 'IOS_FULLSCREEN'
          }
          const bidData:BidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            nativeImageSizeRequested: 'DEFAULT',
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue,
            videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0,
            debug: debugData
          }
          this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_NO_REQUEST, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
        }else if(isFullscreenVideoNode){
          this.debugLog('mobile fullscreen detected using only video node - Will not initiate a bid request because no inserts can be displayed.', [LogType.CXO, LogType.QA]);
          const debugData: {[key:string]:any} = {
            message: 'VIDEO_NODE_FULLSCREEN'
          }
          const bidData:BidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            nativeImageSizeRequested: 'DEFAULT',
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue,
            videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0,
            debug: debugData
          }
          this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_NO_REQUEST, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
        }else if(!isNil(params.stowedBidResponse)){
          this.debugLog('Previously stored bid response is still available to be processed - Will not initiate a new bid request.', [LogType.CXO, LogType.QA]);
          const debugData: {[key:string]:any} = {
            message: 'UNPROCESSED_RESOURCE_AVABILE'
          }
          const bidData:BidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            nativeImageSizeRequested: 'DEFAULT',
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue,
            videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0,
            debug: debugData
          }
          this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_NO_REQUEST, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
        }else if(this.currentDimensions.width <= 10 || this.currentDimensions.height <= 10){
          this.debugLog('Bid request not initiated because current overlay dimensions are too small to support an insert.', [LogType.CXO, LogType.QA]);
          const debugData: {[key:string]:any} = {
            message: 'INVALID_OVERLAY_SIZE'
          }
          const bidData:BidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            nativeImageSizeRequested: 'DEFAULT',
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue,
            videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0,
            debug: debugData
          }
          this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_NO_REQUEST, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
        }else if(!this.forceNextBid && !(isNil(this.getActiveResourceData()) || isNil(this.getCarouselResourceData()))){
          this.debugLog('Bid request not initiated because no insert slots are available for a new resource.', [LogType.CXO, LogType.QA]);
          const debugData: {[key:string]:any} = {
            message: 'NO_INSERT_SLOTS_AVAILABLE'
          }
          const bidData:BidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            nativeImageSizeRequested: 'DEFAULT',
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue,
            videoTime: (!isNil(params.videoElement))?params.videoElement.currentTime:0,
            debug: debugData
          }
          this.analyticsEventDispatcher.sendBidEvent(AnalyticsEventType.BID_NO_REQUEST, BidOriginSourceType.PLATFORM, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), bidData);
        }
        params.currentElementStatus.lastBidTime = (!isNil(params.videoElement))?params.videoElement.currentTime:0;
        if((params.currentElementStatus.timerBasedBidding) && !isNil(this.groupConfig) && this.getCXOActive()){
          params.setNextAutomaticBidTime();
        }
    }

    initiateManualBidRequest() {
        this.debugLog("Manual bid request initiated, checking availability to make request", [LogType.QA, LogType.CXO]);
        const manualBidRequestDelay = this.manualBidRequestTimeout.timeRemainingInSeconds;
        if(manualBidRequestDelay > 0){
          this.debugLog(['Manual bid request is currently delayed, seconds remaining:', manualBidRequestDelay], [LogType.QA, LogType.CXO]);
          this.initiateManualBidRequestTimer.reset(manualBidRequestDelay*1000);
        } else {
          this.debugLog("Initiating manual bid request", [LogType.QA, LogType.CXO]);
          this.forceNextManualBid = true;
          this.forceNextInsert = true;
          this.rebidCount = 0;
          this.manualBidRequestTimeout.reset(10000);
          this.initiateBidRequest();
        }
      }

    attemptRebid(millisecondDelay: number, rebidBannerSizeAllowedDeviation: number, originSource: BidOriginSourceType = null){
        const useOSDBidOrder = (originSource === BidOriginSourceType.OSD && this.osdBidOrder?.length > 0)
        const bidOrderForRebid = useOSDBidOrder ? this.osdBidOrder : this.bidderOrder;
        const bidOrderMessage = useOSDBidOrder ? ' (OSD Bid Order)' : ' (Standard Bid Order)';
        this.rebidCount++;
        if(this.requestedBannerSizes.length > rebidBannerSizeAllowedDeviation){
          this.requestedBannerSizes = [];
        }
        if(this.rebidCount <= (bidOrderForRebid.length-1)){
          this.debugLog(`Attempting rebid ${this.rebidCount} of ${(bidOrderForRebid.length-1)}${bidOrderMessage}. Making new bid request.`, [LogType.CXO, LogType.QA]);
          if(this.isBidTimeAllowed){
            this.forceNextBid = this.forceNextRebid;
            this.initiateBidRequestTimer.reset(millisecondDelay);
          }else{
            this.forceNextRebid = false;
            this.forceNextBid = false;
          }
        }else{
          this.rebidCount = 0;
        }
      }

    get isBidTimeAllowed(): boolean{
        if(this.componentName === ComponentSourceType.OVERLAY){
            const videoElement = (this.getActiveElement() as HTMLVideoElement)
            let bidAllowed: boolean = true;
            if(!isNil(videoElement)){
                if((videoElement.currentTime > videoElement.duration-15) && this._playerConfig?.isLiveStreamPlayer?.toLowerCase() !== "true"){
                    bidAllowed = false;
                }

                if(!isNil(this.groupConfig.neverInsert)){
                this.groupConfig.neverInsert.forEach(timeBlock => {
                    if(bidAllowed && videoElement.currentTime < timeBlock.end && videoElement.currentTime + this.groupConfig.insertDuration > timeBlock.start){
                    bidAllowed = false;
                    }
                })
                }
            }
            return bidAllowed;
        } else {
            return true
        }
    }

    get isFullscreenIOS(): boolean{
        return Boolean(this.isMobileDevice && Boolean(navigator.platform.match(/iPhone|iPod|iPad/)) && Boolean(document.fullscreenElement || document['webkitIsFullScreen'] || document['mozFullScreen'] || document['msFullscreenElement'] || document['webkitCurrentFullScreenElement']))
    }

    get isOSDAvailable(): boolean{
        if(this.osdConfig?.slotRefreshParams?.allowGAMRefresh){
            return !isNil(this.osdConfig);
        }
        return this.osdConfig?.isPrebidAvailable ?? false;
    }

    normalizeOSDConfig(osdConfig: OSDSupplyAccessConfig): OSDSupplyAccessConfig {
        if (this.logManager.getDebugParamValue(DebugURLParams.REMOVE_OSD) === 'true') {
            this.debugLog("Forcing `null` OSD Config per request by URL Parameter", [LogType.QA, LogType.CXO]);
            return null;
        } else {
            this.debugLog(['Normalizing slot refresh params.'], [LogType.CXO, LogType.QA]);
            const slotParams: any = osdConfig.slotRefreshParams;
            if(slotParams.gptAdUnitId?.length > 0){
                slotParams.allowGAMRefresh = true;
                const gto = slotParams.gtagObject;
                slotParams.gtagObject = (typeof gto === 'string' ? window[gto] : gto) ?? window['googletag'];
                if (window.self !== window.top) {
                    const highestWindow = this.availableTopWindow;

                    if(typeof slotParams.gtagObject?.pubads === 'undefined' && typeof highestWindow['googletag'] !== 'undefined' && highestWindow['googletag'] !== null){
                        slotParams.gtagObject = highestWindow['googletag'];
                        this.debugLog(['Google tag object loaded from top window.', slotParams.gtagObject], [LogType.QA, LogType.CXO]);
                    }
                }
                if(!slotParams.gtagObject){
                    this.debugLog(['Googletag object not found on page while adUnitId is provided. Cannot initialize On-Stream Direct.'], [LogType.CXO, LogType.QA]);
                }
            } else {
                this.debugLog('Ad Unit Id empty or missing - Removing gtag object', [LogType.CXO, LogType.QA]);
                slotParams.gtagObject = null;
                slotParams.allowGAMRefresh = false;
            }

            const pbo = slotParams.pbjsObject
            slotParams.pbjsObject = (typeof pbo === 'string' ? window[pbo] : pbo) ?? this.findOnpagePBJS();
            if(!slotParams.pbjsObject) {
                this.debugLog(['PBJS object not found on page. Will remove bidderArray definition.'], [LogType.CXO, LogType.QA]);
                slotParams.bidderArray = null;
            }
            if (slotParams.bidderArray?.length === 0) {
                this.debugLog('Bidder array empty or missing - Removing prebid object', [LogType.CXO, LogType.QA]);
                slotParams.pbjsObject = null;
                slotParams.bidderArray = null;
            }

            if(!this.logManager.getDebugParamValue(DebugURLParams.REMOVE_WATERFALL_BIDDING)) {
                const overrideBidOrder = this.logManager.getDebugParamValue(DebugURLParams.OVERRIDE_OSD_BID_ORDER)?.split('|');
                if (overrideBidOrder?.length > 0) {
                    slotParams.osdBidOrder = overrideBidOrder.map(x => OsdBidOrderOption[x.toUpperCase()]);
                    this.osdBidOrder = slotParams.osdBidOrder;
                    this.debugLog(`OSD_WATERFALL_BIDDING osdBidOrder overriden by debug param ${slotParams.osdBidOrder}`, [LogType.QA, LogType.CXO, LogType.TAM]);
                } else if (this.groupConfig?.osdBidOrder?.length > 0) {
                    slotParams.osdBidOrder = this.groupConfig.osdBidOrder.map(x => OsdBidOrderOption[x]);
                    this.osdBidOrder = slotParams.osdBidOrder;
                    this.debugLog(`OSD_WATERFALL_BIDDING waterfall bidding is active with bidorder ${slotParams.osdBidOrder}`, [LogType.QA, LogType.CXO, LogType.TAM]);
                } else {
                    this.debugLog(`OSD_WATERFALL_BIDDING group config bid order array has no valid options ${slotParams.osdBidOrder}`, [LogType.QA, LogType.CXO, LogType.TAM]);
                }
            } else {
                this.debugLog(`OSD_WATERFALL_BIDDING waterfall has been removed due to url param override`, [LogType.QA, LogType.CXO, LogType.TAM]);
            }

            osdConfig.slotRefreshParams = slotParams;
            osdConfig.availableAdSizes ??= null;
            osdConfig.isPrebidAvailable = !isNil(slotParams.pbjsObject) && !isNil(slotParams.bidderArray);
            this.debugLog(`OSD configuration loaded:`, [LogType.QA, LogType.CXO, LogType.TAM]);
            this.debugLog(`OSD GPT Ad Slot Path = '${osdConfig.slotRefreshParams.gptAdUnitId}'`, [LogType.QA, LogType.CXO, LogType.TAM]);
            this.debugLog([`OSD available ad sizes:`, (osdConfig?.availableAdSizes?.length > 0) ? osdConfig.availableAdSizes : 'ALL'], [LogType.QA, LogType.CXO, LogType.TAM]);
            this.debugLog([`OSD -- is Prebid script (pbjs) available? ${(!(typeof osdConfig?.slotRefreshParams?.pbjsObject === 'undefined' || osdConfig?.slotRefreshParams?.pbjsObject === null)) ? 'YES' : 'NO'}`], [LogType.QA, LogType.CXO, LogType.TAM]);
            this.debugLog([`OSD bidderArray:`, (osdConfig.slotRefreshParams.bidderArray?.length > 0 && Object.keys(osdConfig.slotRefreshParams.bidderArray[0]).length > 0) ? osdConfig.slotRefreshParams.bidderArray : 'NONE'], [LogType.QA, LogType.CXO, LogType.TAM]);
            this.debugLog([`OSD allowGAMRefresh: `, osdConfig.slotRefreshParams.allowGAMRefresh], [LogType.QA, LogType.CXO, LogType.TAM]);

            return osdConfig;
        }
    }

    initializePrebidEventHandlers(pbjsObject, code, pbjsRequested, sizes) {
        pbjsObject.onEvent('bidRequested', (evt) => {
            this.debugLog([`CXO_OSD_REFRESH pbjs bid requested from bidder ${evt?.bidderCode}`, evt, evt?.bids], [LogType.CXO]);
            if (evt?.bids?.length > 0) {
                this.osdProperties.cxoBidInfo.bidders ??= [];
                if (evt.bids[0]?.adUnitCode === code) {
                    this.osdProperties.cxoBidInfo['pbjs_requested_' + evt?.bidderCode] = evt;
                    this.osdProperties.cxoBidInfo.bidders.push(evt.bidderCode);
                }
            }
            if(!pbjsRequested.sent){
                const prebidData:PrebidData = {
                    overlayTime: this.elapsedOverlayTime/1000,
                    activeOverlayTime: this.activeOverlayTime/1000,
                    totalBidsCount: this.bidCount,
                    adOpportunityBidCount: (this.rebidCount + 1),
                    adOpportunityId: this.adOpportunityId,
                    bidRequestId: this.bidRequestId,
                    bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                    componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                    containerViewability: this.containerViewabilityValue
                }
                if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                    prebidData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                }
                this.analyticsEventDispatcher.sendPrebidEvent(AnalyticsEventType.PREBID_REQUEST, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), prebidData);
                pbjsRequested.sent = true;
            }
        });

        pbjsObject.onEvent('noBid', (evt) => {
            if (evt?.adUnitCode === code) {
                this.osdProperties.cxoBidInfo['pbjs_noBid_' + evt?.bidder] = evt;
                this.debugLog([`CXO_OSD_REFRESH pbjs noBid received from bidder ${evt?.bidder}`, evt], [LogType.CXO]);
            }
        });

        pbjsObject.onEvent('bidResponse', (evt) => {
            if (evt?.adUnitCode === code) {
                this.osdProperties.cxoBidInfo['pbjs_response_' + evt?.bidderCode] = evt;
                this.debugLog([`CXO_OSD_REFRESH pbjs bid response received from bidder ${evt?.bidderCode}`, evt], [LogType.CXO]);
            } else {
                //this.debugLog([`pbjs bid response received - Does not match ${evt.adUnitCode} !== ${gptSlotId}`],[LogType.CXO, LogType.QA]);
            }
        });

        pbjsObject.onEvent('bidWon', (evt) => {
            if (evt?.adUnitCode === code) {
                this.osdProperties.cxoBidInfo['pbjs_won_' + evt?.bidderCode] = evt;
                this.osdProperties.cxoBidInfo['pbjs_won'] = evt;
                this.debugLog([`CXO_OSD_REFRESH pbjs bid won received from bidder ${evt?.bidderCode} at $${evt?.cpm}`, evt], [LogType.CXO, LogType.QA]);
                
                const debugData: {[key:string]:any} = {
                    bidder: evt?.bidder || evt?.bidderCode,
                    cpm: evt.cpm,
                    size: evt.size,
                    advertiser: evt?.meta?.advertiserDomains
                }
                const prebidData:PrebidData = {
                    overlayTime: this.elapsedOverlayTime/1000,
                    activeOverlayTime: this.activeOverlayTime/1000,
                    totalBidsCount: this.bidCount,
                    adOpportunityBidCount: (this.rebidCount + 1),
                    adOpportunityId: this.adOpportunityId,
                    bidRequestId: this.bidRequestId,
                    bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                    componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                    containerViewability: this.containerViewabilityValue,
                    debug: debugData
                }
                if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                    prebidData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                }
                this.analyticsEventDispatcher.sendPrebidEvent(AnalyticsEventType.PREBID_WON, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), prebidData);
            }
        });
        this.debugLog([`CXO_OSD_REFRESH cxBidder object initialized and pbjs bidResponse and bidWon events added`, this.osdProperties], [LogType.CXO, LogType.QA]);
    }

    parseBidsBack(bids, code, sizes, pbjsObject, bidData) {
        this.debugLog(['parseBidsBack: ', bids, code, sizes, pbjsObject, bidData], LogType.IMPORTANT);
        const prebidData:PrebidData = {
            overlayTime: this.elapsedOverlayTime/1000,
            activeOverlayTime: this.activeOverlayTime/1000,
            totalBidsCount: this.bidCount,
            adOpportunityBidCount: (this.rebidCount + 1),
            adOpportunityId: this.adOpportunityId,
            bidRequestId: this.bidRequestId,
            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
            containerViewability: this.containerViewabilityValue
        }
        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
            prebidData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
        }
        this.analyticsEventDispatcher.sendPrebidEvent(AnalyticsEventType.PREBID_RESPONSE, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), prebidData);

        if (!!bids[code]?.bids) {
            this.debugLog(['parseBidsBack: filtering bids'], LogType.IMPORTANT);
            bids[code].bids.forEach(bid => {
                const validSizes = sizes.concat([[1, 1]]);
                if (!validSizes.find(x => x.toString() === [bid.width, bid.height].toString())) {
                    bidData.rejectedBids ??= {};
                    Object.keys(bidData).forEach(obj => {
                        if (obj.includes('pbjs_response') && bidData[obj].adId === bid.adId) {
                            bidData.rejectedBids[obj] = bidData[obj];
                            delete (bidData[obj]);
                        }
                    })
                    this.debugLog(['parseBidsBack: mark bid as used: ' + bid.adId], LogType.IMPORTANT);
                    pbjsObject.markWinningBidAsUsed({ adId: bid.adId });
                }
            })
        }
    }

    initiateNoGAMPrebidAuction(osdConfig: OSDSupplyAccessConfig, sizes, bidData, generateInsertCallback, prebidResource: Resource) {
        const {bidderArray, pbjsObject} = osdConfig.slotRefreshParams;
        const availableAdSizes = osdConfig.availableAdSizes ?? [[300, 250],[336, 280],[300, 600],[300, 1080],[234, 60],[320, 50],[468, 60],[728, 90],[970, 90]];
        this.debugLog(['Initiating prebid auction to be handled without GAM', `\availablesizes=`, availableAdSizes, `\nsizes=`, sizes, `\nbidData=`, bidData, '\n'], [LogType.CXO, LogType.QA]);
        const pbjsRequested: {[key:string]: boolean} = {sent: false};
        const prebidCode = 'qx-prebid-slot';

        if (typeof this.osdProperties === 'undefined' || this.osdProperties === null) {
            //Initialization of pbjs events and cx variables setup
            this.osdProperties = {
                cxoAdSlot: null,
                cxoBidInfo: null,
                cxAdUnits: [{
                    code: prebidCode,
                    mediaTypes: {
                        banner: {
                            sizes: availableAdSizes
                        }
                    },
                    bids: bidderArray
                  }]
            }
            this.initializePrebidEventHandlers(pbjsObject, prebidCode, pbjsRequested, sizes)
        }

        const osdProps = this.osdProperties;
        osdProps.cxoBidInfo = bidData;

        let unitSizes: number[][] = osdProps.cxAdUnits[0]?.mediaTypes?.banner?.sizes ?? [[sizes[0], sizes[1]]];
        unitSizes = unitSizes.filter((size) => !!(sizes.find((s: number[]) => (s[0] === size[0] && s[1] === size[1]))));
        osdProps.cxAdUnits[0].mediaTypes.banner.sizes = unitSizes;

        osdProps.cxAdUnits.forEach(unit =>{
            if(typeof unit?.mediaTypes?.banner !== 'undefined' && unit?.mediaTypes?.banner !== null){
                if(typeof prebidResource?.requestedInsertType !== 'undefined' && prebidResource?.requestedInsertType !== null){
                    const insertType:InsertType = prebidResource.requestedInsertType;
                    if(insertType.includes('wing')){
                        unit.mediaTypes.banner.pos = InsertTypePosition.WING;
                    }else if(insertType.includes('flap')){
                        unit.mediaTypes.banner.pos = InsertTypePosition.FLAP;
                    }else if(insertType.includes('fullwidth')){
                        unit.mediaTypes.banner.pos = InsertTypePosition.OVERLAY_FULLWIDTH;
                    }else if(insertType.includes('squeeze')){
                        if(insertType.includes('custom')){
                            unit.mediaTypes.banner.pos = InsertTypePosition.CUSTOM_SQUEEZEBACK;
                        }else{
                            unit.mediaTypes.banner.pos = InsertTypePosition.BANNER_SQUEEZEBACK;
                        }
                    }else if(insertType.includes('overlay')){
                        unit.mediaTypes.banner.pos = InsertTypePosition.OVERLAY_CORNER;
                    }
                }
            }
        })
    
        if (!pbjsObject.adUnits.find(pbu => pbu.code === prebidCode)) {
            pbjsObject.addAdUnits(osdProps.cxAdUnits);
        }
    
        pbjsObject.requestBids({
            bidsBackHandler: (bids) => {
                
                this.parseBidsBack(bids, prebidCode, sizes, pbjsObject, bidData);
                
                const winner = pbjsObject.getHighestUnusedBidResponseForAdUnitCode(prebidCode);
    
                if(winner?.adId) {
                    bidData.pbjs_expected_winner = winner;
                    this.debugLog([`Winning prebid response:`, winner], [LogType.CXO, LogType.QA]);
                }
                generateInsertCallback(winner, prebidResource);
    
            },
            timeout: 1000,
            adUnits: osdProps.cxAdUnits
        });
    }

    initializeOSDRefresh(gptSlotId: string, bidderArray: any[], googletag: any, pbjsObject: any, allowedAdSizes: number[][]): Function {
        this.debugLog('Creating new OSD refresh function.', [LogType.CXO, LogType.QA]);
        let refreshFunction: Function = () => { }
        const optiYieldAvailable = !isNil(window['optiyield']);
        //setInterval(()=>{
        //this.debugLog(['cxBidder: ', this.osdProperties?.cxoAdSlot], [LogType.CXO, LogType.QA])
        //}, 50);
        const gptConsoleLog: Function = (txt: string | any[], type: LogType | LogType[] = [LogType.IMPORTANT])=>{
            this.logManager.evaluateDebugLog(txt, type);
        }

        if (window.self !== window.top) {
            const highestWindow = this.availableTopWindow;

            if(typeof googletag?.pubads === 'undefined' && typeof highestWindow['googletag'] !== 'undefined' && highestWindow['googletag'] !== null && googletag !== highestWindow['googletag']){
                this.osdConfig.slotRefreshParams.gtagObject = highestWindow['googletag'];
                googletag = highestWindow['googletag'];
                this.debugLog(['Google tag object loaded from top window.', googletag], [LogType.QA, LogType.CXO]);
            }
        }

        if (typeof pbjsObject !== 'undefined' && pbjsObject !== null && typeof bidderArray !== 'undefined' && bidderArray !== null) {
            gptConsoleLog('CXO_OSD_REFRESH Prebid Object found - Will utilize prebid in the slot refresh.', [LogType.CXO, LogType.QA]);
            
            const pbjsRequested: {[key:string]: boolean} = {sent: false};
            const qortexBidderCodes = ['qortex', 'catapultx', 'adkernel'];
            const osdBidOrder = this.osdConfig?.slotRefreshParams?.osdBidOrder;

            let refreshTriggered: boolean = false;
            let osdBidOrderIndex: number = 0;

            const getRefreshTriggered = (): boolean=>{
                //gptConsoleLog('CXO_OSD_REFRESH Refresh triggered: ' + refreshTriggered, LogType.IMPORTANT);
                return refreshTriggered;
            }
            const setRefreshTriggered = (bool: boolean)=>{
                //gptConsoleLog('CXO_OSD_REFRESH Refresh triggered: from ' + refreshTriggered + ' to ' + bool, LogType.IMPORTANT);
                refreshTriggered = bool;
            }

            const resetOsdBidOrderIndex = () : void =>{
                osdBidOrderIndex = 0;
            }

            const incrementOsdBidOrderIndex = () : void => {
                osdBidOrderIndex += 1;
            }

            const getCurrentOsdBidOption = () : OsdBidOrderOption => {
                return osdBidOrder?.[osdBidOrderIndex] ?? null;
            }

            refreshFunction = (node, sizes, bidData) => {
                gptConsoleLog([`CXO_OSD_REFRESH pbjs refreshFunction triggered`, `\nnodeId=`, node.id, `\nnode=`, node, `\nsizes=`, sizes, `\nbidData=`, bidData, '\n', this.osdProperties], [LogType.CXO, LogType.QA]);
                pbjsRequested.sent = false;
                setRefreshTriggered(false);

                resetOsdBidOrderIndex();

                if (typeof this.osdProperties === 'undefined' || this.osdProperties === null) {
                    //Initialization of pbjs events and cx variables setup
                    this.osdProperties = {
                        cxoAdSlot: null,
                        cxoBidInfo: null,
                        cxAdUnits: [{
                            code: gptSlotId,
                            mediaTypes: {
                                banner: {
                                    sizes: allowedAdSizes
                                }
                            },
                            bids: bidderArray
                        }]
                    }

                    this.initializePrebidEventHandlers(pbjsObject, gptSlotId, pbjsRequested, sizes);

                    const scopedOSDProperties = this.osdProperties;
                    
                    const sendGAMRequest = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_REQUEST, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                    }
                    const sendGAMResponse = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_RESPONSE, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                    }
                    const sendGAMResponseEmpty = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_RESPONSE_EMPTY, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                        
                    }
                    const sendGAMRenderFailed = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_RENDER_FAIL, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                    }
                    const sendGAMRenderSuccess = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_RENDER_SUCCESS, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                    }
                    const sendGAMSlotLoadSuccess = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_SLOT_LOAD_SUCCESS, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                    }

                    googletag.pubads().addEventListener('slotRequested', function (event) {
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            gptConsoleLog(`CXO_OSD_REFRESH 'slotRequested' cxoAdSlot load requested.`, [LogType.QA, LogType.CXO]);
                            sendGAMRequest();
                            setRefreshTriggered(true);
                        }
                    });
                    googletag.pubads().addEventListener('slotOnload', function (event) {
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            const responseInformation = event.slot.getResponseInformation();
                            osdProps.cxoBidInfo.gptSlotOnload = responseInformation;
                            window.postMessage({
                                target: 'cxo_gpt_prebid',
                                message: 'GPT load complete'
                            }, window.location.protocol + '//' + window.location.host);
                            gptConsoleLog([`CXO_OSD_REFRESH 'slotOnLoad' cxoAdSlot loaded.`, JSON.stringify(responseInformation)], [LogType.QA, LogType.CXO]);
                            sendGAMSlotLoadSuccess();
                        }
                    });

                    const renderEndedTimer: CustomTimeout = this.timers.osdRefreshRenderEndedTimer;
                    const renderEndedCallback: Function = ()=>{this.renderEndedTimeout(sizes[0][0], sizes[0][1])};
                    const renderEndedTimeout: Function = ()=>{this.renderEndedTimeout(sizes[0][0], sizes[0][1])};
                    
                    googletag.pubads().addEventListener('slotResponseReceived', function (event) {
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            const responseInformation = event.slot.getResponseInformation();
                            const adUnitPath = event.slot.getAdUnitPath();
                            const attributeKeys = event.slot.getAttributeKeys();
                            const categoryExclusions = event.slot.getCategoryExclusions();
                            const slotElementId = event.slot.getSlotElementId();
                            const targetingKeys = event.slot.getTargetingKeys();
                            const targeting: { [key: string]: string } = {};
                            targetingKeys.forEach((t: string) => {
                                targeting.t = event.slot.getTargeting(t);
                            })
                            osdProps.cxoBidInfo.gptSlotResponseReceived = responseInformation;
                            if (responseInformation == null) {
                                window.postMessage({
                                    target: 'cxo_gpt_prebid',
                                    message: 'GPT load failed'
                                }, window.location.protocol + '//' + window.location.host);
                                gptConsoleLog([`CXO_OSD_REFRESH 'slotResponseReceived' googletag load failed (no bids).`, JSON.stringify(responseInformation), adUnitPath, slotElementId, targeting, attributeKeys, categoryExclusions], [LogType.QA, LogType.CXO]);
                                sendGAMResponseEmpty();
                                if(!isNil(osdBidOrder)) {
                                    gptConsoleLog(['OSD_WATERFALL_BIDDING attempting rebid on GAM failed response'], [LogType.QA, LogType.CXO]);
                                    prebidRequestBids();
                                }
                            } else {
                                window.postMessage({
                                    target: 'cxo_gpt_prebid',
                                    message: 'GPT response received'
                                }, window.location.protocol + '//' + window.location.host);
                                gptConsoleLog([`CXO_OSD_REFRESH 'slotResponseReceived' googletag non-null response received.`, JSON.stringify(responseInformation), adUnitPath, slotElementId, targeting, attributeKeys, categoryExclusions], [LogType.QA, LogType.CXO]);
                                renderEndedTimer.reset(3000, renderEndedCallback);
                                sendGAMResponse();
                            }
                        }
                    });
                    googletag.pubads().addEventListener('slotRenderEnded', (event) => {
                        gptConsoleLog(`CXO_OSD_REFRESH 'slotRenderEnded'`, [LogType.CXO]);
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            const responseInformation = event.slot.getResponseInformation();
                            osdProps.cxoBidInfo.gptSlotRenderEnded = event;
                            gptConsoleLog([`CXO_OSD_REFRESH 'slotRenderEnded' googletag slot render ended.`, JSON.stringify(responseInformation)], [LogType.CXO]);
                            if (responseInformation == null) {
                                window.postMessage({
                                    target: 'cxo_gpt_prebid',
                                    message: 'GPT load failed'
                                }, window.location.protocol + '//' + window.location.host);
                                gptConsoleLog([`CXO_OSD_REFRESH 'slotRenderEnded' googletag slot render failed.`, JSON.stringify(responseInformation)], [LogType.QA, LogType.CXO]);
                                sendGAMRenderFailed();
                            } else {
                                gptConsoleLog([`CXO_OSD_REFRESH 'slotRenderEnded' googletag slot render successful.`, JSON.stringify(responseInformation)], [LogType.QA, LogType.CXO]);
                                renderEndedTimeout();
                                sendGAMRenderSuccess();
                                gptConsoleLog(['CXO_OSD_REFRESH refresh triggered? ' + getRefreshTriggered()], LogType.IMPORTANT);
                                setRefreshTriggered(true);
                            }
                        }
                    })
                    googletag.pubads().addEventListener('slotVisibilityChanged', function (event) {
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            gptConsoleLog(`CXO_OSD_REFRESH 'slotVisibilityChanged' googletag slot visibility for ${event.slot.getSlotElementId()} changed to ${event.inViewPercentage}%`, LogType.CXO);
                        }
                    });
                    googletag.pubads().addEventListener('impressionViewable', function (event) {
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            gptConsoleLog([`CXO_OSD_REFRESH 'impressionViewable' googletag impression viewable received.`, event.slot.getResponseInformation()]);
                        }
                    });
                }

                const osdProps = this.osdProperties;
                osdProps.cxoBidInfo = bidData;

                if (osdProps.cxoAdSlot !== null) {
                    gptConsoleLog(osdProps.cxoAdSlot.getSlotElementId?.(), [LogType.CXO, LogType.QA]);
                    if (osdProps.cxoAdSlot.getSlotElementId?.() !== node.id) {
                        const existingSlotId = osdProps.cxoAdSlot.getSlotElementId?.();
                        const slotDestroyed = googletag.destroySlots([osdProps.cxoAdSlot]);
                        gptConsoleLog([`CXO_OSD_REFRESH Node id ${node.id} does not match current slot elementId ${existingSlotId}. Destroying existing googletag cxoAdSlot (was destroyed? ${slotDestroyed})`], [LogType.CXO, LogType.QA]);
                        osdProps.cxoAdSlot = null;
                    } else {
                        gptConsoleLog([`CXO_OSD_REFRESH Node id ${node.id} matches current slot elementId ${osdProps.cxoAdSlot.getSlotElementId?.()}. Refreshing existing googletag cxoAdSlot`], [LogType.CXO, LogType.QA]);
                        this.dispatchOverlayEvent({
                            type: OverlayEventType.OVERLAY_RESET_ACTIVE_OSD,
                            uid: this.getActiveResourceData().uniqueId
                        })
                    }
                }

                // PWT detection for MINUT.LY groups wfmz.com / 9and10news.com
                const pwt = (['212761', '216333', '216665'].includes(this.groupConfig.publisherId) && ['VMboHiu99kmqe22kI6m3ig', 'C6aZR2nNAkSLsiKVPwunhg'].includes(this.groupId)) ? ((document.querySelector('.tvp-host-element') as HTMLIFrameElement)?.contentWindow?.['PWT'] ?? window?.['PWT']) : null;
                
                if(!isNil(pwt)){
                    this.debugLog([`PWT detected: ${this.groupConfig.publisherId} - ${this.groupId}`, pwt], LogType.IMPORTANT);
                }

                if (osdProps.cxoAdSlot == null) {
                    googletag.cmd.push(function () {
                        osdProps.cxoAdSlot = googletag.defineSlot(gptSlotId, sizes, node.id).addService(googletag.pubads());

                        //pbjsObject.setTargetingForGPTAsync([gptSlotId], (slot)=>{return ()=>{console.log('setting targeting on ' + gptSlotId, slot.getAdUnitPath(), Boolean(gptSlotId === slot.getAdUnitPath())); if(gptSlotId === slot.getAdUnitPath()){return true;}else{return false;}}});

                        googletag.pubads().enableSingleRequest();
                        //googletag.pubads().disableInitialLoad();
                        googletag.enableServices();

                        gptConsoleLog(`CXO_OSD_REFRESH 'googletag.display'`, [LogType.QA, LogType.CXO, LogType.IMPORTANT]);
                        googletag.display(node);
                    });
                }
                
                gptConsoleLog([`CXO_OSD_REFRESH defined googletag cxoAdSlot - ${gptSlotId}, ${sizes}, ${node.id}`], [LogType.CXO, LogType.QA]);

                osdProps.cxoAdSlot.clearTargeting();

                var filteredAdUnits: any[] = osdProps.cxAdUnits;
                const resetAdSizes: any[] = [];

                filteredAdUnits.forEach(unit => {
                    resetAdSizes.push({
                        adUnit: unit,
                        originalSizes: [...unit?.mediaTypes?.banner?.sizes]
                    })
                    //console.log(`GPT SIZES: Original ad sizes: ${JSON.stringify(unit?.mediaTypes?.banner?.sizes)}`);
                    let unitSizes: number[][] = unit?.mediaTypes?.banner?.sizes ?? [[sizes[0], sizes[1]]];
                    unitSizes = unitSizes.filter((size) => !!(sizes.find((s: number[]) => (s[0] === size[0] && s[1] === size[1]))));
                    unit.mediaTypes.banner.sizes = unitSizes;
                    
                    if(typeof unit?.mediaTypes?.banner !== 'undefined' && unit?.mediaTypes?.banner !== null){
                        if(typeof node.id !== 'undefined' && node.id !== null){
                            const insertType:InsertType = node.id;
                            if(insertType.includes('wing')){
                                unit.mediaTypes.banner.pos = InsertTypePosition.WING;
                            }else if(insertType.includes('flap')){
                                unit.mediaTypes.banner.pos = InsertTypePosition.FLAP;
                            }else if(insertType.includes('fullwidth')){
                                unit.mediaTypes.banner.pos = InsertTypePosition.OVERLAY_FULLWIDTH;
                            }else if(insertType.includes('squeeze')){
                                if(insertType.includes('custom')){
                                    unit.mediaTypes.banner.pos = InsertTypePosition.CUSTOM_SQUEEZEBACK;
                                }else{
                                    unit.mediaTypes.banner.pos = InsertTypePosition.BANNER_SQUEEZEBACK;
                                }
                            }else if(insertType.includes('overlay')){
                                unit.mediaTypes.banner.pos = InsertTypePosition.OVERLAY_CORNER;
                            }
                        }
                    }
                    
                    //console.log(`GPT SIZES: Filtered ad sizes: ${JSON.stringify(unit?.mediaTypes?.banner?.sizes)}`);
                })

                if(optiYieldAvailable) {
                    this.osdProperties.cxAdUnits[0].optiYield = {
                        adUnitPath : gptSlotId,
                        divId : node.id
                    }
                }

                if (typeof pbjsObject.adUnits.find(pbu => pbu.code === gptSlotId) === 'undefined') {
                    if(optiYieldAvailable){
                        this.debugLog('Setting price floors through OptiYield', LogType.IMPORTANT);
                        window['optiyield'].cmd.push(function () {
                            osdProps.cxAdUnits = window['optiyield'].setPrebidFloors(osdProps.cxAdUnits);
                        });
                    }
                    pbjsObject.addAdUnits(osdProps.cxAdUnits);
                }

                let responseCount: number = 0;
                let responsesRequired: number = 2;
                const refreshTimeoutDelay: number = 2000;
                let forcedRefresh = false;
                let refreshTimeout = setTimeout(()=>{
                    if(!getRefreshTriggered()){
                        gptConsoleLog(`CXO_OSD_REFRESH googletag.refresh called by timeout with ${responseCount} of ${responsesRequired} responses.`, LogType.IMPORTANT);
                        responseCount = responsesRequired;
                        forcedRefresh = true;
                        googleTagRefresh();
                    }
                }, refreshTimeoutDelay);
                
                const sendTAMRequest = ()=>{
                    const tamData:TAMData = {
                        overlayTime: this.elapsedOverlayTime/1000,
                        activeOverlayTime: this.activeOverlayTime/1000,
                        totalBidsCount: this.bidCount,
                        adOpportunityBidCount: (this.rebidCount + 1),
                        adOpportunityId: this.adOpportunityId,
                        bidRequestId: this.bidRequestId,
                        bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                        componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                        containerViewability: this.containerViewabilityValue
                    }
                    if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                        tamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                    }
                    this.analyticsEventDispatcher.sendTAMEvent(AnalyticsEventType.TAM_REQUEST, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), tamData);
                }
                const sendTAMResponse = (bids)=>{
                    const tamData:TAMData = {
                        overlayTime: this.elapsedOverlayTime/1000,
                        activeOverlayTime: this.activeOverlayTime/1000,
                        totalBidsCount: this.bidCount,
                        adOpportunityBidCount: (this.rebidCount + 1),
                        adOpportunityId: this.adOpportunityId,
                        bidRequestId: this.bidRequestId,
                        bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                        componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                        containerViewability: this.containerViewabilityValue,
                        debug: bids
                    }
                    if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                        tamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                    }
                    this.analyticsEventDispatcher.sendTAMEvent(AnalyticsEventType.TAM_RESPONSE, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), tamData);
                }

                const apstag:any = window['apstag'];
                if(typeof apstag?.fetchBids !== 'undefined'){
                    if(!this.groupConfig.zoneModifications.includes(ZoneModifications.DISABLE_TAM)){
                        try{
                            gptConsoleLog(`CXO_OSD_REFRESH TAM apstag bids requested.`, [LogType.QA, LogType.CXO]);
                            sendTAMRequest();
                            let tamSlots:any = [{
                                slotID:node.id,
                                slotName:gptSlotId,
                                sizes:sizes
                            }]
                            if(optiYieldAvailable) {
                                tamSlots[0].optiYield = {
                                    adUnitPath : gptSlotId,
                                    divId : node.id
                                }
                                window['optiyield'].cmd.push(function () {
                                    tamSlots = window['optiyield'].setAmazonFloors(tamSlots);
                                });
                            }
                            apstag.fetchBids({
                                slots:tamSlots,
                                timeout: 1000
                            }, (bids)=>{
                                responseCount++;
                                gptConsoleLog([`CXO_OSD_REFRESH apstag bids response ${responseCount}.`, bids], [LogType.QA, LogType.CXO]);
                                let bidSuccess: boolean = false;
                                bids.forEach(bid =>{
                                    if(!!bid?.amzniid){
                                        gptConsoleLog(`CXO_OSD_REFRESH TAM bid success (${bid?.size}): amziid=${bid.amzniid}`, [LogType.QA, LogType.CXO]);
                                        bidSuccess = true;
                                    }
                                })
                                if(bidSuccess){
                                    sendTAMResponse(bids);
                                }else{
                                    gptConsoleLog(`CXO_OSD_REFRESH TAM bid failed - no results.`, [LogType.QA, LogType.CXO]);
                                }
                                googletag.cmd.push(()=>{
                                    apstag.setDisplayBids();
                                    googleTagRefresh();
                                })
                            })
                        }catch(err){
                            gptConsoleLog(`CXO_OSD_REFRESH TAM error on bid request.`, [LogType.QA, LogType.CXO]);
                            responsesRequired--;
                        }
                    }else{
                        gptConsoleLog(`CXO_OSD_REFRESH TAM disabled with ZoneModification 'disable-tam'. Will not make TAM bid request.`, [LogType.QA, LogType.CXO]);
                        responsesRequired--;
                    }
                }else{
                    responsesRequired--;
                }
                
                const prebidRequestBids = (initialRequest: boolean = false) => {
                    const {code, mediaTypes, bids} = this.osdProperties.cxAdUnits[0];
                    const currentAdunits = new Object({code, mediaTypes});
                    const osdBidOption = getCurrentOsdBidOption();
                    
                    if(isNil(osdBidOption) && osdBidOrder?.length > 0) {
                        gptConsoleLog(['OSD_WATERFALL_BIDDING bid order has reached its end and new bids will not be requested'], [LogType.QA, LogType.CXO]);
                    } else {
                        switch (osdBidOption) {
                            case OsdBidOrderOption.QORTEX_EXCLUDED:
                                currentAdunits['bids'] = bids.filter(b => !qortexBidderCodes.includes(b['bidder']));
                                break;
                            case OsdBidOrderOption.QORTEX_ONLY:
                                currentAdunits['bids'] = bids.filter(b => qortexBidderCodes.includes(b['bidder']));
                                break;
                            case OsdBidOrderOption.ALL:
                                currentAdunits['bids'] = bids;
                                break;
                            default:
                                currentAdunits['bids'] = bids;
                                break;
                        } 

                        if (isNil(osdBidOption)) {
                            gptConsoleLog(['CXO_OSD_REFRESH pbjs requested bids', bids], [LogType.QA, LogType.CXO]);
                        } else {
                            gptConsoleLog([`OSD_WATERFALL_BIDDING pbjs bids requested with type ${osdBidOption}`, currentAdunits['bids']], [LogType.QA, LogType.CXO]);
                            incrementOsdBidOrderIndex();
                        }
                        
                        if(!isNil(pwt)){
                            if (typeof pwt.requestBids === 'function') {
                                pwt.initAdserverSet = false;
                                this.debugLog('OpenWrap (PWT) requestBids.', LogType.QA);
                                pwt.requestBids(
                                    pwt.generateConfForGPT([osdProps.cxoAdSlot]),
                                    function(adUnitsArray) {
                                        pwt.addKeyValuePairsToGPTSlots(adUnitsArray);
                                        googleTagRefresh();
                                    }
                                );
                            }
                        }else{
                            pbjsObject.requestBids({
                                bidsBackHandler: (bids) => {
                                    let hasValidBidsBack = false;
                                    
                                    if (!!bids[gptSlotId]?.bids) {
                                        bids[gptSlotId].bids.forEach(bid => {
                                            const validSizes = sizes.concat([[1, 1]]);
                                            if (!validSizes.find(x => x.toString() === [bid.width, bid.height].toString())) {
                                                bidData.rejectedBids ??= {};
                                                Object.keys(bidData).forEach(obj => {
                                                    if (obj.includes('pbjs_response') && bidData[obj].adId === bid.adId) {
                                                        bidData.rejectedBids[obj] = bidData[obj];
                                                        delete (bidData[obj]);
                                                    }
                                                })
                                                pbjsObject.markWinningBidAsUsed({ adId: bid.adId });
                                            } else if (!hasValidBidsBack) {
                                                hasValidBidsBack = true;
                                            }
                                        })
                                    }
        
                                    const prebidData:PrebidData = {
                                        osdBidType: osdBidOption,
                                        overlayTime: this.elapsedOverlayTime/1000,
                                        activeOverlayTime: this.activeOverlayTime/1000,
                                        totalBidsCount: this.bidCount,
                                        adOpportunityBidCount: (this.rebidCount + 1),
                                        adOpportunityId: this.adOpportunityId,
                                        bidRequestId: this.bidRequestId,
                                        bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                                        componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                                        containerViewability: this.containerViewabilityValue
                                    }
                                    if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                                        prebidData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                                    }
                                    this.analyticsEventDispatcher.sendPrebidEvent(AnalyticsEventType.PREBID_RESPONSE, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), prebidData);
                                    
                                    Object.keys(bidData).forEach(obj => {
                                        if (obj.includes('pbjs_response')) {
                                            bidData.pbjs_expected_winner = (bidData?.pbjs_expected_winner?.cpm > bidData[obj]?.cpm && typeof bidData[obj]?.cpm !== 'undefined') ? bidData.pbjs_expected_winner : bidData[obj];
                                        }
                                    })
        
                                    if( !isNil(getCurrentOsdBidOption()) && !hasValidBidsBack && !forcedRefresh) {
                                        gptConsoleLog(['OSD_WATERFALL_BIDDING rebidding due to no valid bids back', bids], [LogType.QA, LogType.CXO]);
                                        prebidRequestBids(initialRequest);
                                    } else {
                                        if(initialRequest && responseCount != responsesRequired) responseCount++; 
                                        if (!this.osdConfig.slotRefreshParams.osdBidOrder) {
                                            gptConsoleLog([`CXO_OSD_REFRESH prebid bids response ${responseCount}.`, bids]);
                                        } else {
                                            gptConsoleLog([`OSD_WATERFALL_BIDDING prebid bids response, waterfall bid of type ${osdBidOption}.`, bids]);
                                        }
        
                                        pbjsObject.setTargetingForGPTAsync([gptSlotId], (slot) => {
                                            return () => {
                                                gptConsoleLog(['CXO_OSD_REFRESH - setting targeting on ' + gptSlotId, slot.getAdUnitPath(), Boolean(gptSlotId === slot.getAdUnitPath())], LogType.CXO);
                                                return (gptSlotId === slot.getAdUnitPath())
                                            }
                                        });
            
                                        if (this.groupId === '17fW31pTika7HMf64JpPqQ') {
                                            gptConsoleLog(`CXO_OSD_REFRESH setTargeting for BJPenn - 'pubg_hb_src' = 'pubgenius'`);
                                            osdProps.cxoAdSlot.setTargeting('pubg_hb_src', 'pubgenius');
                                        }
        
                                        googleTagRefresh();
                                    }
                                    
                                },
                                timeout: 1000,
                                adUnits: [currentAdunits]
                            });
                        }
                    }   
                }

                prebidRequestBids(true);

                const googleTagRefresh = ()=>{
                    
                    gptConsoleLog(`CXO_OSD_REFRESH googleTagRefresh called with ${responseCount} of ${responsesRequired} responses.`, LogType.CXO);

                    if(!getRefreshTriggered()){
                        if(responseCount === responsesRequired){
                            gptConsoleLog(`CXO_OSD_REFRESH googleTagRefresh ${responseCount} of ${responsesRequired} responses received. Refreshing googletag.`, [LogType.QA, LogType.CXO]);
                            setRefreshTriggered(true);   
                            if(optiYieldAvailable){
                                window['optiyield'].cmd.push(function () {
                                    window['optiyield'].setGptFloors();
                                });
                            }
                            googletag.cmd.push(function () {
                                gptConsoleLog(`CXO_OSD_REFRESH 'googletag.pubads().refresh'`, [LogType.QA, LogType.CXO, LogType.IMPORTANT]);
                                googletag.pubads().refresh([osdProps.cxoAdSlot]);
                            });
                            clearTimeout(refreshTimeout);
                        }
                    }else if (!isNil(osdBidOrder) && !forcedRefresh) {
                        googletag.cmd.push(function () {
                            gptConsoleLog(`OSD_WATERFALL_BIDDING refreshing new waterfall bids back`, [LogType.QA, LogType.CXO, LogType.IMPORTANT]);
                            googletag.pubads().refresh([osdProps.cxoAdSlot]);
                        });
                    } else {                        
                        gptConsoleLog(`CXO_OSD_REFRESH googleTagRefresh refresh triggered already.`, [LogType.QA, LogType.CXO]);
                    }
                }

                setTimeout(() => {
                    resetAdSizes.forEach(unit => {
                        unit.adUnit.mediaTypes.banner.sizes = unit.originalSizes;
                        //console.log(`GPT SIZES: Reset ad sizes: ${JSON.stringify(unit?.adUnit?.mediaTypes?.banner?.sizes)}`);
                    })
                }, 1200)
            }
        } else {
            if (typeof pbjsObject === 'undefined' || pbjsObject === null) {
                this.debugLog('Prebid Object NOT found - Will only use googletag in the slot refresh.', [LogType.CXO, LogType.QA]);
            } else {
                this.debugLog('Prebid bidder array not defined - Will only use googletag in the slot refresh.', [LogType.CXO, LogType.QA]);
            }
            refreshFunction = (node, sizes, bidData) => {
                gptConsoleLog([`CXO_OSD_REFRESH refreshFunction triggered`, `\nnode=`, node, `\nsizes=`, sizes, `\nbidData=`, bidData, '\n', this.osdProperties], [LogType.CXO, LogType.QA]);
                if (typeof this.osdProperties === 'undefined' || this.osdProperties === null) {
                    //Initialization of pbjs events and cx variables setup
                    this.osdProperties = {
                        cxoAdSlot: null,
                        cxoBidInfo: null,
                        cxAdUnits: [{
                            code: gptSlotId,
                            mediaTypes: {
                                banner: {
                                    sizes: allowedAdSizes
                                }
                            },
                            bids: bidderArray
                        }]
                    }

                    const scopedOSDProperties: OSDProperties = this.osdProperties;
                    const renderEndedTimer: CustomTimeout = this.timers.osdRefreshRenderEndedTimer;
                    const renderEndedCallback: Function = ()=>{this.renderEndedTimeout(sizes[0][0], sizes[0][1])};
                    const renderEndedTimeout: Function = ()=>{this.renderEndedTimeout(sizes[0][0], sizes[0][1])};

                    const sendGAMRequest = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_REQUEST, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                    }
                    const sendGAMResponse = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_RESPONSE, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                    }
                    const sendGAMResponseEmpty = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_RESPONSE_EMPTY, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                        
                    }
                    const sendGAMRenderFailed = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_RENDER_FAIL, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                    }
                    const sendGAMRenderSuccess = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_RENDER_SUCCESS, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                    }
                    const sendGAMSlotLoadSuccess = ()=>{
                        const gamData:GAMData = {
                            overlayTime: this.elapsedOverlayTime/1000,
                            activeOverlayTime: this.activeOverlayTime/1000,
                            totalBidsCount: this.bidCount,
                            adOpportunityBidCount: (this.rebidCount + 1),
                            adOpportunityId: this.adOpportunityId,
                            bidRequestId: this.bidRequestId,
                            bannerSizeRequested: `${sizes[0][0]}x${sizes[0][1]}`,
                            componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                            containerViewability: this.containerViewabilityValue
                        }
                        if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                            gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
                        }
                        this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_SLOT_LOAD_SUCCESS, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
                    }

                    googletag.pubads().addEventListener('slotRequested', function (event) {
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            gptConsoleLog(`CXO_OSD_REFRESH 'slotRequested' cxoAdSlot load requested.`);
                            sendGAMRequest();
                        }
                    });
                    googletag.pubads().addEventListener('slotOnload', function (event) {
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            const responseInformation = event.slot.getResponseInformation();
                            osdProps.cxoBidInfo.gptSlotOnload = responseInformation;
                            window.postMessage({
                                target: 'cxo_gpt_prebid',
                                message: 'GPT load complete'
                            }, window.location.protocol + '//' + window.location.host);
                            gptConsoleLog([`CXO_OSD_REFRESH 'slotOnload' cxoAdSlot loaded.`, JSON.stringify(responseInformation)]);
                            sendGAMSlotLoadSuccess();
                        }
                    });
                    googletag.pubads().addEventListener('slotResponseReceived', function (event) {
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            const responseInformation = event.slot.getResponseInformation();
                            osdProps.cxoBidInfo.gptSlotResponseReceived = responseInformation;
                            if (responseInformation == null) {
                                window.postMessage({
                                    target: 'cxo_gpt_prebid',
                                    message: 'GPT load failed'
                                }, window.location.protocol + '//' + window.location.host);
                                gptConsoleLog([`CXO_OSD_REFRESH 'slotResponseReceived' googletag load failed (no bids).`, JSON.stringify(responseInformation)], [LogType.QA, LogType.CXO]);
                                
                                sendGAMResponseEmpty();
                            } else {
                                gptConsoleLog([`CXO_OSD_REFRESH 'slotResponseReceived' googletag non-null response received.`, JSON.stringify(responseInformation)], [LogType.QA, LogType.CXO]);
                                renderEndedTimer.reset(3000, renderEndedCallback);
                                sendGAMResponse();
                            }
                        }
                    });
                    googletag.pubads().addEventListener('slotRenderEnded', (event) => {
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            const responseInformation = event.slot.getResponseInformation();
                            osdProps.cxoBidInfo.gptSlotRenderEnded = event;
                            gptConsoleLog([`CXO_OSD_REFRESH 'slotRenderEnded' googletag slot render ended.`, responseInformation], LogType.CXO);
                            if (responseInformation == null) {
                                window.postMessage({
                                    target: 'cxo_gpt_prebid',
                                    message: 'GPT load failed'
                                }, window.location.protocol + '//' + window.location.host);
                                gptConsoleLog([`CXO_OSD_REFRESH 'slotRenderEnded' googletag slot render failed.`, responseInformation], [LogType.QA, LogType.CXO]);
                                sendGAMRenderFailed();
                            } else {
                                window.postMessage({
                                    target: 'cxo_gpt_prebid',
                                    message: 'GPT response received'
                                }, window.location.protocol + '//' + window.location.host);
                                gptConsoleLog([`CXO_OSD_REFRESH 'slotRenderEnded' googletag slot render successful.`, responseInformation], [LogType.QA, LogType.CXO]);
                                renderEndedTimeout();
                                sendGAMRenderSuccess();
                            }
                        }
                    })
                    googletag.pubads().addEventListener('slotVisibilityChanged', function (event) {
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            gptConsoleLog([`CXO_OSD_REFRESH 'slotVisibilityChanged' googletag slot visibility for ${event.slot.getSlotElementId()} changed to ${event.inViewPercentage}%`]);
                        }
                    });
                    googletag.pubads().addEventListener('impressionViewable', function (event) {
                        if (event.slot === scopedOSDProperties.cxoAdSlot) {
                            gptConsoleLog([`CXO_OSD_REFRESH 'impressionViewable' googletag impression viewable received.`, event.slot.getResponseInformation()]);
                        }
                    });
                }

                const osdProps: OSDProperties = this.osdProperties;
                osdProps.cxoBidInfo = bidData;

                if (osdProps.cxoAdSlot !== null) {
                    if (osdProps.cxoAdSlot.getSlotElementId?.() !== node.id) {
                        const existingSlotId = osdProps.cxoAdSlot.getSlotElementId?.();
                        const slotDestroyed = googletag.destroySlots([osdProps.cxoAdSlot]);
                        gptConsoleLog([`CXO_OSD_REFRESH Node id ${node.id} does not match current slot elementId ${existingSlotId}. Destroying existing googletag cxoAdSlot (was destroyed? ${slotDestroyed})`], [LogType.CXO, LogType.QA]);
                        osdProps.cxoAdSlot = null;
                    } else {
                        gptConsoleLog([`CXO_OSD_REFRESH Node id ${node.id} matches current slot elementId ${osdProps.cxoAdSlot.getSlotElementId?.()}. Refreshing existing googletag cxoAdSlot`], [LogType.CXO, LogType.QA]);
                    }
                }

                googletag.cmd.push(function () {
                    if (osdProps.cxoAdSlot == null) {
                        osdProps.cxoAdSlot = googletag.defineSlot(gptSlotId, sizes, node.id).addService(googletag.pubads());
                        gptConsoleLog(`CXO_OSD_REFRESH defined googletag adSlot - ${gptSlotId}, ${sizes}, ${node.id}`);

                        googletag.enableServices();
                        googletag.pubads().enableSingleRequest();
                        googletag.pubads().disableInitialLoad();

                        googletag.display(node);
                    } else {
                        gptConsoleLog(`CXO_OSD_REFRESH reusing defined googletag adSlot - ${gptSlotId}, ${sizes}, ${node.id}`);
                    }


                    googletag.pubads().refresh([osdProps.cxoAdSlot]);
                })

                gptConsoleLog(`googletag bids requested`, [LogType.CXO, LogType.QA]);
            }
        }
        return refreshFunction;
    }

    renderEndedTimeout(renderedWidth:number = 0, renderedHeight: number = 0) {
        if (this.timers.osdRefreshRenderEndedTimer.timeRemainingInSeconds === 0) {
            this.debugLog(`CXO_OSD_REFRESH render ended (failed) load time: ${this.timers.osdRefreshRenderEndedTimer.definedCallbackInMillseconds}ms.`, [LogType.INSERT, LogType.QA]);
            this.debugLog(`CXO_OSD_REFRESH_FAILED.  ${this.osdProperties.cxoBidInfo.gptSlotResponseReceived?.advertiserId} - ${this.osdProperties.cxoBidInfo.gptSlotResponseReceived?.campaignId} - ${this.osdProperties.cxoBidInfo.gptSlotResponseReceived?.creativeId}`, [LogType.INSERT, LogType.QA]);
            window.postMessage({
                target: 'cxo_gpt_prebid',
                message: 'GPT render failed'
            }, window.location.protocol + '//' + window.location.host);
            const gamData:GAMData = {
                overlayTime: this.elapsedOverlayTime/1000,
                activeOverlayTime: this.activeOverlayTime/1000,
                totalBidsCount: this.bidCount,
                adOpportunityBidCount: (this.rebidCount + 1),
                adOpportunityId: this.adOpportunityId,
                bidRequestId: this.bidRequestId,
                bannerSizeRequested: `${renderedWidth}x${renderedHeight}`,
                componentDimensions: {width:this.currentDimensions.width, height:this.currentDimensions.height},
                containerViewability: this.containerViewabilityValue
            }
            if(this.componentName === ComponentSourceType.OVERLAY && !isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime)){
                gamData.videoTime = (!isNil((this.getActiveElement() as HTMLVideoElement)?.currentTime))?(this.getActiveElement() as HTMLVideoElement).currentTime:0;
            }
            this.analyticsEventDispatcher.sendGAMEvent(AnalyticsEventType.GAM_RENDER_FAIL, BidOriginSourceType.OSD, this.currentWindowLocation.href, Boolean(this.foundUID !== "NOT_FOUND"), this.getPlayerMode(), gamData);
        } else {
            this.debugLog(`CXO_OSD_REFRESH render ended (success) load time: ${this.timers.osdRefreshRenderEndedTimer.timeElapsedSinceStartTime}ms.`, [LogType.INSERT, LogType.QA]);
            this.debugLog(`CXO_OSD_REFRESH_SUCCESS. ${this.osdProperties.cxoBidInfo.gptSlotResponseReceived?.advertiserId} - ${this.osdProperties.cxoBidInfo.gptSlotResponseReceived?.campaignId} - ${this.osdProperties.cxoBidInfo.gptSlotResponseReceived?.creativeId}`, [LogType.INSERT, LogType.QA]);
            this.timers.osdRefreshRenderEndedTimer.cancel();
        }
    }

    dispatchOverlayEvent(params: Object) {
        const insertEvent: CustomEvent = new CustomEvent(OverlayEventType.EVENT_FROM_OVERLAY, {
            bubbles: false,
            detail: params
        })
        if (!isNil(this.getActiveElement())) {
            this.getActiveElement().dispatchEvent(insertEvent)
        } else {
            this.debugLog(`dispatchOverlayElement - Video element is null. Dispatching event from ${this.componentName}.`, [LogType.CXO, LogType.QA]);
            this.componentElement.dispatchEvent(insertEvent);
        }
    }

    cleanOSDResponseData(data: {[key:string]:any}): {[key:string]:any}{
        data ??= {};
        let debugData: {[key:string]:any}  = {
            osdData: data
        }

        const refererInfo: {[key: string]: any} = this.sharedFunctions.removeKeys(this.sharedFunctions.findKey(debugData.osdData, "refererInfo"), ["legacy"]);
        const ortb2: {[key: string]: any} = this.sharedFunctions.removeKeys(this.sharedFunctions.findKey(debugData.osdData, "ortb2"), ["eids"]);
        const auctionStart: {[key: string]: any} = this.sharedFunctions.findKey(debugData.osdData, "auctionStart");
        const auctionId: {[key: string]: any} = this.sharedFunctions.findKey(debugData.osdData, "auctionId");
        const adUnits: {[key: string]: any} = this.sharedFunctions.removeKeys(this.getBidAdUnitMapping(debugData.osdData), ["bidId"]);
        debugData.osdData = this.sharedFunctions.removeKeys(debugData.osdData, ["userId", "userIdAsEids", "metrics", "noBids", "ad", "eids", "pbsWurl", "refererInfo", "ortb2Imp", "ortb2", "adUnitsS2SCopy", "adUnits", "auctionStart", "auctionId", "deferBilling", "adId", "statusMessage", "width", "height", "gdprConsent", "pbjs_expected_winner"]);
        debugData.osdData = this.sharedFunctions.removeKeys(debugData.osdData, Object.keys(debugData.osdData).filter(key => key.includes('pbjs_requested_') || key.includes('pbjs_response_') || key.includes('pbjs_noBid_') || key.includes('pbjs_won_')));
        debugData.osdData.adUnits = adUnits;
        debugData.osdData.refererInfo = refererInfo;
        debugData.osdData.ortb2 = ortb2;
        debugData.osdData.auctionStart = auctionStart;
        debugData.osdData.auctionId = auctionId;
        debugData.osdData.isBackfill = debugData.osdData?.gptSlotRenderEnded?.['isBackfill'] ?? debugData.osdData?.gptSlotResponseReceived?.['isBackfill'] ?? false;
        debugData.osdData.isEmpty = debugData.osdData?.gptSlotRenderEnded?.['isEmpty'] ?? false;


        const cleanData = (obj:{[key:string]:{}})=>{
            const currentData = {};
            for(var d in obj){
                if(typeof obj[d] !== 'function'){
                    if(typeof obj[d] === 'object'){
                        const cleanedData = cleanData(obj[d]);
                        if(Object.keys(cleanedData)?.length > 0){
                            currentData[d] = cleanedData;
                        }
                    }else{
                        currentData[d] = obj[d];
                    }
                }
            }
            return currentData;
        }

        const returnData:{[key:string]:{}} = {
            osdData: cleanData(debugData.osdData)
        };
        return returnData;
    }

    determineInsertTypeOptions(): string[]{
        const insertConfigs: InsertConfig = this.groupConfig.insertConfig;
        let optionsList: string[]
        const isCTV = (this.groupConfig.zoneType === 'ctv-on-stream' || this.logManager.getDebugParamValue(DebugURLParams.FORCE_CTV_MODE) === 'true')
        switch (this.getPlayerMode()) {
          case PlayerMode.MOBILE_FLOATING:
            optionsList = this.removeProhibitedInsertTypes(insertConfigs.allowedInsertsMobileFloating.map(item => item['insertType']))
            if (optionsList.length > 0) {
              this.debugLog(['determineInsertTypeOptions --mobileFloatingPlayer --allowedList', optionsList])
            } else {
              const defaultOptionsList: string[] = (isCTV)?
                [this.currentInserts.ctvOverlayCorner, this.currentInserts.ctvSqueeze]:
                (!this.currentDimensions.isFullscreen)?this.removeProhibitedInsertTypes([this.currentInserts.nativeFlap, this.currentInserts.nativeWing]):this.removeProhibitedInsertTypes([this.currentInserts.nativeOverlayFullwidth]);
              optionsList = defaultOptionsList;
              this.debugLog(['determineInsertTypeOptions --mobileFloatingPlayer --noAllowedInserts\nDefault insert options set:', optionsList], [LogType.QA, LogType.CXO]);
            }
            break
          case PlayerMode.DESKTOP_FLOATING:
            optionsList = this.removeProhibitedInsertTypes(insertConfigs.allowedInsertsDesktopFloating.map(item => item['insertType']))
    
            if (optionsList.length > 0) {
              this.debugLog(['determineInsertTypeOptions --desktopFloatingPlayer --allowedList', optionsList])
            } else {
              const defaultOptionsList: string[] = (isCTV)?
                [this.currentInserts.ctvOverlayCorner, this.currentInserts.ctvSqueeze]:
                (!this.currentDimensions.isFullscreen)?this.removeProhibitedInsertTypes([this.currentInserts.nativeFlap, this.currentInserts.nativeWing]):this.removeProhibitedInsertTypes([this.currentInserts.nativeOverlayFullwidth]);
              optionsList = defaultOptionsList;
              this.debugLog(['determineInsertTypeOptions --desktopFloatingPlayer --noAllowedInserts\nDefault insert options set:', optionsList], [LogType.QA, LogType.CXO]);
            }
            break
          case PlayerMode.MOBILE_ONSTREAM:
            optionsList = this.removeProhibitedInsertTypes(insertConfigs.allowedInsertsMobileOnStream.map(item => item['insertType']))
            if (optionsList.length > 0) {
              this.debugLog(['determineInsertTypeOptions --mobileOnStreamPlayer --allowedList', optionsList])
            } else {
              const defaultOptionsList: string[] = (isCTV)?
                [this.currentInserts.ctvOverlayCorner, this.currentInserts.ctvSqueeze]:
                this.removeProhibitedInsertTypes([this.currentInserts.nativeOverlayFullwidth]);
              optionsList = defaultOptionsList;
              this.debugLog(['determineInsertTypeOptions --mobileOnStreamPlayer --noAllowedInserts\nDefault insert options set:', optionsList], [LogType.QA, LogType.CXO]);
            }
            break
          case PlayerMode.DESKTOP_ONSTREAM:
            optionsList = this.removeProhibitedInsertTypes(insertConfigs.allowedInsertsDesktopOnStream.map(item => item['insertType']))
            if (optionsList.length > 0) {
              this.debugLog(['determineInsertTypeOptions --desktopOnStreamPlayer --allowedList', optionsList])
            } else {
              const defaultOptionsList: string[] = (isCTV)?
                [this.currentInserts.ctvOverlayCorner, this.currentInserts.ctvSqueeze]:
                this.removeProhibitedInsertTypes([this.currentInserts.nativeOverlayFullwidth, this.currentInserts.nativeOverlayIcon]);
              optionsList = defaultOptionsList;
              this.debugLog(['determineInsertTypeOptions --desktopOnStreamPlayer --noAllowedInserts\nDefault insert options set:', optionsList], [LogType.QA, LogType.CXO]);
            }
            break
          case PlayerMode.MOBILE_INARTICLE:
            optionsList = this.removeProhibitedInsertTypes(insertConfigs.allowedInsertsMobileInArticle.map(item => item['insertType']))
            if (optionsList.length > 0) {
              this.debugLog(['determineInsertTypeOptions --mobileInArticlePlayer --allowedList', optionsList])
            } else {
              const defaultOptionsList: string[] = (isCTV)?
                [this.currentInserts.ctvOverlayCorner, this.currentInserts.ctvSqueeze]:
                this.removeProhibitedInsertTypes([this.currentInserts.nativeOverlayFullwidth]);
              optionsList = defaultOptionsList;
              this.debugLog(['determineInsertTypeOptions --mobileInArticlePlayer --noAllowedInserts\nDefault insert options set:', optionsList], [LogType.QA, LogType.CXO]);
            }
            break
          case PlayerMode.DESKTOP_INARTICLE:
            optionsList = this.removeProhibitedInsertTypes(insertConfigs.allowedInsertsDesktopInArticle.map(item => item['insertType']))
            if (optionsList.length > 0) {
              this.debugLog(['determineInsertTypeOptions --desktopInArticlePlayer --allowedList', optionsList])
            } else {
              const defaultOptionsList: string[] = (isCTV)?
                [this.currentInserts.ctvOverlayCorner, this.currentInserts.ctvSqueeze]:
                this.removeProhibitedInsertTypes([this.currentInserts.nativeOverlayFullwidth, this.currentInserts.nativeOverlayIcon]);
              optionsList = defaultOptionsList;
              this.debugLog(['determineInsertTypeOptions --desktopInArticlePlayer --noAllowedInserts\nDefault insert options set:', optionsList], [LogType.QA, LogType.CXO]);
            }
            break
          default:
            optionsList = [
            ]
            break
        }
        return optionsList
    }

    captureAnimationFrame(){
        if(this.timers.framerateTimer.repeatCount > 0){
            this.frameArray.push((new Date()).getTime());
            if(this.frameArray.length > 1000){
                this.frameArray = this.frameArray.slice(-1000);
            }
            requestAnimationFrame(()=>{this.captureAnimationFrame()});
        }
    }

    updateAdOpportunityId(){
        this.adOpportunityIdentifier = this.generateUniqueId;
    }

    updateBidRequestId(){
        this.bidRequestIdentifier = this.generateUniqueId;
    }
    
    percentageDifference(a: number, b: number){
        return 100 * Math.abs((a-b)/((a+b)*.5)) || 0;
    }

    getBidAdUnitMapping(auctionData: {[key: string]: any}): {[key: string]: any}[] {
        const mapping: {[key: string]: any}[] = [];
    
        // Get all keys that start with pbjs_requested_
        const requestedBidders = Object.keys(auctionData).filter(key => key.startsWith('pbjs_requested_'));
    
        // Process requested bids
        requestedBidders.forEach(bidderKey => {
            const request = auctionData[bidderKey];
            if (!request.bids) return;
            
            Object.values(request.bids).forEach((bid: {[key: string]: any}) => {
                const sizes = Object.values(bid.sizes || {}).map(size => [size[0], size[1]])
                    .sort((a, b) => {
                        const areaComparison = (a[0] * a[1]) - (b[0] * b[1]);
                        if (areaComparison === 0) {
                            return a[0] - b[0];
                        }
                        return areaComparison;
                    });
    
                const bidderInfo = {
                    adUnitCode: bid.adUnitCode,
                    bidderCode: bid.bidder,
                    bidId: bid.bidId,
                    sizes: sizes.map(size => size.join('x')).join(', ')
                };
    
                mapping.push(bidderInfo);
            });
        });
    
        // Process bid responses
        Object.keys(auctionData)
            .filter(key => key.startsWith('pbjs_response_'))
            .forEach(responseKey => {
                const response = auctionData[responseKey];
                if (!response.adUnitCode) return;
                
                const matchingBidder = mapping.find(
                    bidderInfo => bidderInfo.bidId === response.requestId
                );
                
                if (matchingBidder) {
                    matchingBidder.cpm = response.cpm;
                    matchingBidder.size = response.size;
                    matchingBidder.currency = response.currency;
                    if (response.meta?.advertiserDomains) {
                        matchingBidder.advertiser = (typeof response.meta.advertiserDomains === 'string') ? response.meta.advertiserDomains : (response.meta.advertiserDomains.length > 1) ? response.meta.advertiserDomains.join(', ') : response.meta.advertiserDomains[0];
                    }
                }
            });
                
        // Process expected winner
        Object.keys(auctionData)
        .filter(key => key.startsWith('pbjs_expected_winner'))
        .forEach(responseKey => {
            const response = auctionData[responseKey];
            const matchingBidder = mapping.find(
                bidderInfo => bidderInfo.bidId === response.requestId
            );
            
            if (matchingBidder) {
                matchingBidder.expectedWinner = true;
            }
        });
    
        // Add winning bid information
        if (auctionData.pbjs_won) {
            const winningBid = auctionData.pbjs_won;
            const matchingBidder = mapping.find(
                bidderInfo => bidderInfo.bidId === winningBid.requestId
            );
            if (matchingBidder) {
                matchingBidder.won = true;
            }
        }
    
        return mapping;
    }


    get isOverlay(): boolean{
        return this.componentName === ComponentSourceType.OVERLAY;
    } 

    get currentInserts(): CurrentInsertMap{
        return this._currentInserts;
    }

    get frameRate(): number{
        const currentTime: number = (new Date()).getTime();
        const cutoffTime: number = currentTime-2000;
        this.frameArray = this.frameArray.slice(this.frameArray.findIndex((i)=>{return i > cutoffTime}));
        const earliestTime: number = ((currentTime-this.frameArray[0]) > 1000)?((currentTime-this.frameArray[0])/1000):1;
        return this.frameArray.length/earliestTime;
    }
    
    get initiateManualBidRequestTimer(): CustomTimeout{
        return this.timers.initiateManualBidRequestTimer;
    }

    set initiateManualBidRequestTimer(timer: CustomTimeout){
        this.timers.initiateManualBidRequestTimer = timer;
    }

    get manualBidRequestTimeout(): CustomTimeout{
        return this.timers.manualBidRequestTimeout;
    }

    set manualBidRequestTimeout(timer: CustomTimeout){
        this.timers.manualBidRequestTimeout = timer;
    }

    get carouselBidTimer(): CustomTimeout{
        return this.timers.carouselBidTimer;
    }

    set carouselBidTimer(timer: CustomTimeout){
        this.timers.carouselBidTimer = timer;
    }

    get initiateBidRequestTimer(): CustomTimeout{
        return this.timers.initiateBidRequestTimer;
    }

    set initiateBidRequestTimer(timer: CustomTimeout){
        this.timers.initiateBidRequestTimer = timer;
    }

    get automaticBidTimer(): CustomTimeout{
        return this.timers.automaticBidTimer;
    }

    set automaticBidTimer(timer: CustomTimeout){
        this.timers.automaticBidTimer = timer;
    }

    get adOpportunityId(): string{
        return this.adOpportunityIdentifier;
    }

    get bidRequestId(): string{
        return this.bidRequestIdentifier;
    }

    get analyticsSessionId(): string{
        return this.analyticsSessionIdentifier;
    }

    get currentWindowLocation(): Location {
        let windowLocation: Location = window.location;
        if (window.self !== window.top) {
            try {
                this.debugLog(`Current overlay is within an iframe. Top window url=${window.top.location.href}`, LogType.CXO);
                windowLocation = window.top.location;
            } catch (err) {
                this.debugLog([`Current overlay is within an iframe, but unable to access top window url. Using url: ${windowLocation.href}`], LogType.CXO);
            }
        }
        return windowLocation;
    }

    // Generate analytics user identifier.
    get generateUniqueId(): string {
        return this.sharedFunctions.generateUniqueId();
    }

    get elapsedOverlayTime(): number {
        return (new Date()).getTime() - this.componentStartTime;
    }

    get activeOverlayDate(): number {
        return this._activeOverlayDate;
    }

    set activeOverlayDate(time: number) {
        this._activeOverlayDate = time;
    }

    get activeOverlayTime(): number {
        const currentTime: number = (new Date()).getTime();
        if (this.activeOverlayDate !== 0) {
            this._activeOverlayRuntime += currentTime - this.activeOverlayDate;
            this.activeOverlayDate = currentTime;
        }
        this.activeOverlayDate = (!document.hidden) ? currentTime : 0;

        return this._activeOverlayRuntime;
    }

    get tcString(): string {
        return this._tcString;
    }

    get uspString(): string {
        return this._uspString;
    }

    get gppString(): string {
        return this._gppString;
    }

    get gppSids(): number[] {
        return this._gppSids;
    }

    get eidsIdentityHub(): any {
        return this._eidsIdentityHub;
    }

    get eidsintentIQ(): any {
        return this._eidsintentIQ;
    }

    get gdprApplies(): number {
        return this._gdprApplies;
    }

    get isMobileDevice(): boolean {
        let check = false;
        (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; })(navigator.userAgent || navigator.vendor);
        return check;
    }

    get isInAppBrowser(): boolean {
        const userAgent: string = navigator.userAgent || navigator.vendor;
        const isFacebook: boolean = !!userAgent.toLowerCase().match(/fbios|fb_iab/ig);
        const isTwitter: boolean = !!userAgent.toLowerCase().match(/twitter/ig);
        const isInstagram: boolean = !!userAgent.toLowerCase().match(/instagram/ig);
        const isSnapchat: boolean = !!userAgent.toLowerCase().match(/snapchat/ig);
        const isWeChat: boolean = !!userAgent.toLowerCase().match(/micromessenger/ig);
        return (isFacebook || isTwitter || isInstagram || isSnapchat || isWeChat) ? true : false;
    }

    isBlockedRegion(): boolean {
      const regions:string[] = this.groupConfig.regions;
      const includeRegions:boolean = this.groupConfig.includeRegions;
      const regionInfo:UserRegionInfo = this.groupConfig.regionInfo;
      if (!!this.logManager.getDebugParamValue(DebugURLParams.OVERRIDE_GEO_BLOCKING)) {
        this.debugLog(["Overriding geo blocking due to url parameter"], [LogType.CXO, LogType.QA])
        return false
      } else if(isNil(regions) || isNil(includeRegions)) {
        this.debugLog(["Region blocking is not configured for this group"], [LogType.CXO, LogType.QA])
        return false
      } else if (isNil(regionInfo)) {
        //adding this in case we want to create a flag that makes this return true 
        //for example: we want to block certain regions AND we want to block if we cant determine the region
        this.debugLog(["Unable to determine the region of the client to apply region blocking config"], [LogType.CXO, LogType.QA]);
        return false;
      } else if(regions.length === 0){
        return false;
      } else {
        const regionFound = regions.includes(regionInfo.regionCode) || regions.includes(regionInfo.country);
        return includeRegions ? !regionFound : regionFound;
      }
    }

    get isDomainBlockedForInApp(): boolean {
        const blockedInAppDomains: string[] = ['factinate', 'roughmaps', 'moneyawaits', 'motorsearches', 'medical-news', 'sportscroll', 'motor-junkie', 'historycollection', 'moneyppl', 'sciencesensei', 'homeaddict'];
        return !!this.currentWindowLocation.hostname.toLowerCase().match((new RegExp(blockedInAppDomains.join('|'), 'gi')));
    }

    get isAndroidWebView(): boolean {
        let isAWV = false;
        if (!navigator.userAgent.includes('AmazonWebAppPlatform')) {
            if (/(Version\/\d+.*\/\d+.0.0.0 Mobile|; ?wv)/i.test(navigator.userAgent)) {
                this.debugLog('Android webview navigator detected: ' + navigator.userAgent, [LogType.CXO, LogType.QA])
                isAWV = true;
                // eslint-disable-next-line dot-notation
            } else if (typeof window['Android'] !== 'undefined') {
                this.debugLog('Android webview window.Android variable detected.', [LogType.CXO, LogType.QA]);
                isAWV = true;
                // eslint-disable-next-line no-prototype-builtins
            } else if (window.hasOwnProperty('Android ')) {
                this.debugLog('Android webview window.hasOwnProperty("Android ") detected.', [LogType.CXO, LogType.QA]);
                isAWV = true
            }
        }
        return isAWV;
    }

    get availableTopWindow(): Window {
        let returnWindow: Window = window;
        if (window.self !== window.top) {
            try {
                if (window.top.location.href !== null) {
                    returnWindow = window.top;
                }
            } catch (err: any) {
                this.debugLog(['Unable to access window.top. Serving from inside an unsafe iframe.', err], [LogType.QA, LogType.IMPORTANT]);
            }
        }
        return returnWindow;
    }

    set groupConfig(gc: GroupConfig){
        this._groupConfig = gc;
        if(!isNil(this.groupConfig?.prebidObjectIdentifier) && !isNil(this.osdConfig?.slotRefreshParams)){
            this.osdConfig.slotRefreshParams.pbjsObject = this.findOnpagePBJS();
        }
        const debugParams = this.groupConfig?.debugParameters ?? '';
        if(debugParams !== '') {
            this.logManager.extractQueryParams(debugParams);
            this.debugLog(['Setting debug params from group config', debugParams],[LogType.QA]);
        }
        this.processGroupConfig();
    }

    get groupConfig(): GroupConfig{
        return this._groupConfig;
    }
    
    set groupConfigsProcessed(value: boolean){
        this._groupConfigsProcessed = value;
    }

    get isGroupConfigsProcessed(): boolean{
        return this._groupConfigsProcessed;
    }
    
    get isADXAvailable(): boolean{
        if(!isNil(this.groupConfig.gptAdUnitPageUrl) && this.groupConfig.gptAdUnitPageUrl.length > 0 && !isNil(this.groupConfig.gptAdUnitPath) && this.groupConfig.gptAdUnitPath.length > 0 && !this.isCTV){
            return true;
        }
        return false;
    }
    
    get isBlockedURL(): boolean{
        let isBlocked: boolean = false;
        if(!isNil(this.groupConfig.blockedUrls)){
        const windowLocation: Location = this.currentWindowLocation;
        this.groupConfig.blockedUrls.forEach(url => {
            const isWildcardURL: boolean = url.endsWith('?');
            this.debugLog([`Comparing blocked URL: '${url}' against current page url: '${windowLocation.href}'. ${(!isWildcardURL)?'URL must be exact match.':'URL does not require exact match.'}`], [LogType.CXO, LogType.QA]);
            if(isWildcardURL){
            url = url.substring(0, url.length-1);
            }
            const blockedURL: URL = (url.includes('http'))?new URL(url):new URL('https://'+url);
            if(windowLocation.hostname === blockedURL.hostname && ((windowLocation.pathname === blockedURL.pathname || windowLocation.pathname === blockedURL.pathname+'/' || windowLocation.pathname+'/' === blockedURL.pathname) || (isWildcardURL && windowLocation.pathname.includes(blockedURL.pathname)))){
            this.debugLog([`Current page (${windowLocation.href}) is a blocked url (${blockedURL.hostname + blockedURL.pathname}) for this group.`], [LogType.QA, LogType.CXO]);
            isBlocked = true;
            }
        })
        if(!isBlocked){
            this.debugLog([`Current page (${windowLocation.href}) is NOT in the blocked url list for this group.`], [LogType.QA, LogType.CXO]);
        }
        }
        return isBlocked;
    }
}